/**
 * ÓRDENES DE TRABAJO: LISTADO, GENERACIÓN Y DETALLE
 *
 * Es una pantalla de trabajo, no un panel: filtros arriba, tabla densa,
 * clic en la fila para abrir. El color se reserva para lo que hay que
 * mirar —anuladas, emisiones mayores que 1, órdenes ya sustituidas— y no
 * se reparte por toda la tabla.
 *
 * Esta primera versión OBSERVA. No se ejecuta, no se asigna y no se
 * cambian estados: eso es otro vertical y todavía no está decidido quién
 * hace qué.
 */
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  type Centro,
  type Cliente,
  type DatosOtManual,
  type LineaOt,
  type OperarioResumen,
  type Ot,
  type OtDetalle,
  type OtGlobal,
  type OtManualCreada,
  type PaginaOt,
  type PaginaOtGlobal,
  type Plan,
  type PropuestaTareasOt,
  type ResultadoAccionLinea,
  type ResultadoAccionMasiva,
  type TareaPropuestaOt,
  type TipoLimpieza,
  type Ubicacion,
} from '../api.ts';
import {
  ahoraParaInput,
  alternarOrden,
  Avatar,
  CabeceraOrdenable,
  Cargando,
  Cifra,
  EmptyState,
  Error as ErrorCaja,
  EstadoLinea,
  EstadoOrigen,
  EstadoOt,
  etiquetaEstadoOt,
  etiquetaOrigen,
  fecha,
  fechaHora,
  FiltrosResponsive,
  hoyLocal,
  nombresOperarios,
  SinResultados,
  useDatos,
  useEsMovil,
  Vacio,
} from '../componentes/base.tsx';
import { RevisionMultiOt } from '../componentes/confirmacion-multi-ot.tsx';
import { DocumentosAplicables } from '../componentes/documentos-aplicables.tsx';
import { Evidencias } from '../componentes/evidencias.tsx';
import { SeccionNoConformidades } from '../componentes/no-conformidades.tsx';
import { BotonReportarIncidenciaLinea, SeccionIncidenciasOt } from '../componentes/calidad.tsx';
import { Shell } from '../componentes/shell.tsx';
import { VerificacionMultiOt } from '../componentes/verificacion-multi-ot.tsx';
import { useSesion } from '../contexto.tsx';
import { AvisoConsumosPendientes, ConsumosOtSeccion, useConsumosOt } from './consumos-ot.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { useConectividadAlEntrar } from '../pwa.ts';
import { useCentro } from './centro.tsx';

type Dir = 'asc' | 'desc';

/**
 * FASE 5.7-C C02 — entrar a cualquier listado/detalle de OT sin conexión
 * pedía datos al servidor y se quedaba en un error de red sin salida,
 * obligando a saber navegar a mano a "Trabajar sin conexión". `/offline`
 * ya sabe listar (y, con `idOt`, detallar) las OT descargadas a este
 * dispositivo — se redirige ahí en vez de duplicar esa lectura aquí.
 */
const MENSAJE_OFFLINE_OTS = 'Estás trabajando sin conexión. Mostrando las órdenes disponibles en este dispositivo.';

//  FASE 4.3 (BUG 4): antes faltaban VERIFICADA/CERRADA/REABIERTA — desde que
//  BUG 2 (agregación de estado) está corregido, las OT sí llegan a esos
//  estados de verdad, y el filtro tiene que poder pedirlos. F10.0: fuera
//  ASIGNADA (ots_estado_check no la admite) y VALIDADA/RECHAZADA (legado
//  0031 que ningún flujo produce): opciones que nunca devolvían nada.
//  estados.ts conserva sus etiquetas por si aparece una fila histórica.
const ESTADOS = [
  'PLANIFICADA',
  'EN_CURSO',
  'COMPLETADA',
  'VERIFICADA',
  'CERRADA',
  'REABIERTA',
  'NO_REALIZADA',
  'ANULADA',
] as const;

/**
 * TARJETA DE OT — REPRESENTACIÓN MÓVIL (FASE 3.3-D4)
 *
 * Misma OT, mismo dato, otra forma: `TablaOts`/aquí no filtra, ordena ni
 * pagina nada — eso sigue siendo enteramente de la página, que le pasa el
 * mismo array ya filtrado/ordenado/paginado que recibe la tabla.
 *
 * POR QUÉ NO ES UN ÚNICO <Link> ENVOLVIENDO TODA LA TARJETA
 *
 * FASE4 quiere poder confirmar o verificar una línea o una OT entera SIN
 * entrar siempre a su ficha, y eventualmente en lote (selección múltiple +
 * checkbox + acción contextual). Si la tarjeta entera fuera un `<Link>`,
 * meter un checkbox dentro sería un enlace-dentro-de-control interactivo y
 * habría que rehacer la tarjeta desde cero. Con la acción de abrir como un
 * `<Link>` EXPLÍCITO dentro de `.tarjeta-ot-cabecera` (no la tarjeta
 * entera), un checkbox futuro sólo tiene que insertarse como primer hijo
 * de ese mismo `<div>` flex — la tarjeta no cambia de forma.
 *
 * El link de apertura lleva relleno propio (`.tarjeta-ot-codigo`) para
 * seguir siendo un target cómodo de una mano sin necesitar que la tarjeta
 * entera sea clicable.
 */

type OrdenOtsContextual = 'codigo' | 'fechaPlan' | 'estado';

function TarjetaOtContextual({ ot }: { ot: Ot }): ReactNode {
  return (
    <li className="tarjeta-ot">
      <div className="tarjeta-ot-cabecera">
        <Link to={`/ots/${ot.id}`} className="tarjeta-ot-codigo" aria-label={`Abrir OT ${ot.codigo}`}>
          {ot.codigo}
        </Link>
        <span className="fila" style={{ gap: 6 }}>
          <EstadoOt estado={ot.estado} />
          {ot.sustituida && <span className="etiqueta info">SUSTITUIDA</span>}
        </span>
      </div>
      <div className="tarjeta-ot-datos">
        <div className="tarjeta-ot-fecha">{fecha(ot.fechaPlan)}</div>
        <div className="suave pequena">
          Sala <span className="cod">{ot.salaCodigo}</span>
        </div>
        <div className="suave pequena">
          {ot.planCodigo === null ? 'Sin plan' : `${ot.planCodigo} v${String(ot.planVersion)}`}
        </div>
      </div>
      <div className="tarjeta-ot-secundario">
        <EstadoOrigen origen={ot.origen} />
        {ot.emision > 1 && <span className="etiqueta info">Emisión {ot.emision}</span>}
      </div>
    </li>
  );
}

/**
 * Card global: mismo criterio que `TarjetaOtContextual`, con Cliente/
 * Centro añadidos como TEXTO, no como `<Link>`. Cliente y Centro ya son
 * navegables desde la propia ficha de la OT (cabecera y panel Datos,
 * 3.3-C2); repetirlos aquí como enlaces añadiría dos targets más por
 * tarjeta en una lista táctil donde el objetivo casi siempre es abrir la
 * OT, no saltar al cliente. Si en el futuro se ve una necesidad real de
 * saltar a Cliente/Centro desde el listado, es un cambio local a esta
 * función, no una reestructuración.
 */
function TarjetaOtGlobal({
  ot,
  seleccionable = false,
  seleccionada = false,
  onSeleccionar,
}: {
  ot: OtGlobal;
  seleccionable?: boolean;
  seleccionada?: boolean;
  onSeleccionar?: () => void;
}): ReactNode {
  return (
    <li className="tarjeta-ot">
      <div className="tarjeta-ot-cabecera">
        {seleccionable && (
          <input
            type="checkbox"
            checked={seleccionada}
            onChange={onSeleccionar}
            aria-label={`Seleccionar ${ot.codigo}`}
            style={{ width: 20, height: 20 }}
          />
        )}
        <Link to={`/ots/${ot.id}`} className="tarjeta-ot-codigo" aria-label={`Abrir OT ${ot.codigo}`}>
          {ot.codigo}
        </Link>
        <span className="fila" style={{ gap: 6 }}>
          <EstadoOt estado={ot.estado} />
          {ot.sustituida && <span className="etiqueta info">SUSTITUIDA</span>}
        </span>
      </div>
      <div className="tarjeta-ot-datos">
        <div className="tarjeta-ot-fecha">{fecha(ot.fechaPlan)}</div>
        <div>{ot.clienteRazonSocial}</div>
        <div className="suave pequena">
          {ot.centroDescripcion} <span className="cod">{ot.centroCodigo}</span>
        </div>
        <div className="suave pequena">
          {ot.planCodigo === null ? 'Sin plan' : `${ot.planCodigo} v${String(ot.planVersion)}`}
        </div>
      </div>
      <div className="tarjeta-ot-secundario">
        <EstadoOrigen origen={ot.origen} />
        {ot.emision > 1 && <span className="etiqueta info">Emisión {ot.emision}</span>}
      </div>
    </li>
  );
}

/**
 * `fechaPlan DESC` porque es el `ORDER BY o.fecha_plan DESC,
 * o.sala_codigo_snapshot, o.emision` que el backend ya aplicaba antes de
 * esta fase — con estos valores por defecto, `orderBy`/`orderDir` ni
 * siquiera se mandan (ver más abajo), así que el backend toma la misma
 * rama que preserva ese orden literal.
 */
const DEFAULTS_OTS_CONTEXTUAL = {
  fechaDesde: '', fechaHasta: '', estado: '', idSala: '', origen: '',
  orden: 'fechaPlan', dir: 'desc', pagina: '1',
} as const;

export function PaginaOts(): ReactNode {
  const { centro } = useCentro();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const conectividad = useConectividadAlEntrar();
  //  `generar` es un flag de un solo uso (llega por enlace desde
  //  Planificación): se lee al montar, aparte de `useListaUrl`, que no
  //  conoce ni gestiona esa clave.
  const [parametrosIniciales] = useSearchParams();
  const [generando, setGenerando] = useState(parametrosIniciales.get('generar') === '1');
  const [creandoOt, setCreandoOt] = useState(false);

  const lista = useListaUrl(DEFAULTS_OTS_CONTEXTUAL, 'pagina');

  const fechaDesde = lista.valor('fechaDesde');
  const fechaHasta = lista.valor('fechaHasta');
  const filtroEstado = lista.valor('estado');
  const idSala = lista.valor('idSala');
  const origen = lista.valor('origen');
  const orden = lista.valor('orden') as OrdenOtsContextual;
  const dir = lista.valor('dir') as Dir;
  const pagina = Number(lista.valor('pagina'));

  function ordenarPor(columna: OrdenOtsContextual): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  OT-UX-DEBT-01 (DEBT-ZERO-01): en la representación móvil (tarjetas,
  //  ver `useEsMovil`) la preferencia de negocio es ordenar por código de
  //  OT — `ots.codigo` es monotónico de verdad (secuencia zero-rellenada,
  //  0021), así que ordenar por él no miente sobre cronología. Sólo se
  //  aplica cuando nadie ha elegido explícitamente otro orden: en cuanto
  //  `orden`/`dir` se alejan del default (clic en una cabecera, o un
  //  enlace con `?orden=` en la URL), esa elección manda igual en
  //  cualquier ancho. El escritorio no cambia: sigue viendo `fechaPlan`.
  const esMovil = useEsMovil();
  const usaOrdenPorDefecto = orden === DEFAULTS_OTS_CONTEXTUAL.orden && dir === DEFAULTS_OTS_CONTEXTUAL.dir;
  const ordenEfectivo: OrdenOtsContextual = esMovil && usaOrdenPorDefecto ? 'codigo' : orden;
  const dirEfectivo: Dir = esMovil && usaOrdenPorDefecto ? 'desc' : dir;

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `orden`/`dir`/`pagina`. Esta
  //  pantalla no tiene `q`, así que no hay nada más que excluir.
  const contadorFiltros = [
    fechaDesde !== DEFAULTS_OTS_CONTEXTUAL.fechaDesde,
    fechaHasta !== DEFAULTS_OTS_CONTEXTUAL.fechaHasta,
    filtroEstado !== DEFAULTS_OTS_CONTEXTUAL.estado,
    idSala !== DEFAULTS_OTS_CONTEXTUAL.idSala,
    origen !== DEFAULTS_OTS_CONTEXTUAL.origen,
  ].filter(Boolean).length;

  const consulta = new URLSearchParams();
  if (fechaDesde !== '') consulta.set('fechaDesde', fechaDesde);
  if (fechaHasta !== '') consulta.set('fechaHasta', fechaHasta);
  if (filtroEstado !== '') consulta.set('estado', filtroEstado);
  if (idSala !== '') consulta.set('idSala', idSala);
  if (origen !== '') consulta.set('origen', origen);
  //  `orderBy`/`orderDir` van juntos o no van: si sólo se omitiera uno de
  //  los dos porque su valor coincide con el default, el backend recibiría
  //  el otro suelto y —al no reconocer el orden por defecto— aplicaría SU
  //  propio valor seguro (ASC), no el `desc` que de verdad se pidió.
  //  Se manda el EFECTIVO (que en móvil, sin elección explícita, es
  //  codigo/desc): tabla y tarjetas leen el mismo array ya ordenado por
  //  el backend — no hay "orden de tabla" y "orden de tarjetas" aparte.
  if (ordenEfectivo !== DEFAULTS_OTS_CONTEXTUAL.orden || dirEfectivo !== DEFAULTS_OTS_CONTEXTUAL.dir) {
    consulta.set('orderBy', ordenEfectivo);
    consulta.set('orderDir', dirEfectivo);
  }
  consulta.set('pagina', String(pagina));
  consulta.set('porPagina', '50');

  const estado = useDatos(
    () => api.get<PaginaOt>(`/api/centros/${centro.id}/ots?${consulta.toString()}`),
    [centro.id, consulta.toString()],
  );

  const totalPaginas = Math.max(1, Math.ceil((estado.datos?.total ?? 0) / 50));

  if (conectividad === 'OFFLINE') {
    return <Navigate to="/offline" replace state={{ avisoOffline: MENSAJE_OFFLINE_OTS }} />;
  }

  return (
    <>
      {generando && (
        <GenerarOts
          idCentro={centro.id}
          alCerrar={() => {
            setGenerando(false);
            estado.recargar();
          }}
        />
      )}
      {creandoOt && (
        <FormularioNuevaOt
          centroFijo={centro}
          alCancelar={() => setCreandoOt(false)}
          alCrear={(ot) => navegar(`/ots/${ot.id}`)}
        />
      )}

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Órdenes de trabajo</h2>
          <div className="fila">
            <span className="suave pequena">{estado.datos?.total ?? 0} en total</span>
            {puede('ots:crear_manual') && !creandoOt && (
              <button className="pequeno" onClick={() => setCreandoOt(true)}>
                Nueva OT
              </button>
            )}
            {puede('ots:generar') && !generando && (
              <button className="principal pequeno" onClick={() => setGenerando(true)}>
                Generar próximas OTs
              </button>
            )}
          </div>
        </div>

        <FiltrosResponsive contador={contadorFiltros}>
          <div className="campo">
            <label htmlFor="f-desde">Desde</label>
            <input
              id="f-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => lista.fijar('fechaDesde', e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="f-hasta">Hasta</label>
            <input
              id="f-hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => lista.fijar('fechaHasta', e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="f-estado">Estado</label>
            <select
              id="f-estado"
              value={filtroEstado}
              onChange={(e) => lista.fijar('estado', e.target.value)}
            >
              <option value="">Todos</option>
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {etiquetaEstadoOt(e)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="f-sala">Sala</label>
            <select
              id="f-sala"
              value={idSala}
              onChange={(e) => lista.fijar('idSala', e.target.value)}
            >
              <option value="">Todas</option>
              {(estado.datos?.salas ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.codigo}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="f-origen">Origen</label>
            <select
              id="f-origen"
              value={origen}
              onChange={(e) => lista.fijar('origen', e.target.value)}
            >
              <option value="">Todos</option>
              {['PLAN', 'PUNTUAL', 'EXTRA', 'CORRECTIVA'].map((o) => (
                <option key={o} value={o}>
                  {etiquetaOrigen(o)}
                </option>
              ))}
            </select>
          </div>
          {!lista.esPorDefecto && (
            <button className="pequeno" onClick={lista.limpiar}>
              Limpiar filtros
            </button>
          )}
        </FiltrosResponsive>

        <div className="panel-cuerpo sin-relleno">
          {estado.cargando && estado.datos === null ? (
            <Cargando />
          ) : estado.error !== null ? (
            <ErrorCaja error={estado.error} reintentar={estado.recargar} />
          ) : (estado.datos?.filas.length ?? 0) === 0 ? (
            //  UX-1C: vacío real ≠ vacío por filtros.
            lista.esPorDefecto
              ? <EmptyState titulo="No hay órdenes de trabajo" descripcion="Si el centro tiene plan vigente, genera el horizonte de 8 semanas para emitirlas." />
              : <SinResultados />
          ) : (
            <>
              {/*
                FASE 3.3-D4: misma `estado.datos.filas` para las dos
                representaciones — nada se filtra, ordena ni pagina dos
                veces. `.tabla-ots-desktop`/`.lista-ots-movil` alternan por
                CSS según el ancho (ver `estilos.css`); la oculta queda en
                `display: none`, fuera del foco y de la lectura por
                accesibilidad, no sólo tapada visualmente.
              */}
              <div className="tabla-desliza tabla-ots-desktop">
                <table>
                  <thead>
                    <tr>
                      <CabeceraOrdenable
                        texto="OT"
                        columna="codigo"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('codigo')}
                      />
                      <CabeceraOrdenable
                        texto="Fecha"
                        columna="fechaPlan"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('fechaPlan')}
                      />
                      <th>Sala</th>
                      <CabeceraOrdenable
                        texto="Estado"
                        columna="estado"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('estado')}
                      />
                      <th>Origen</th>
                      <th>Versión</th>
                      <th className="num">Emisión</th>
                      <th className="num">Líneas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(estado.datos?.filas ?? []).map((o) => (
                      <tr key={o.id} className="pulsable" onClick={() => navegar(`/ots/${o.id}`)}>
                        <td className="cod">{o.codigo}</td>
                        <td>{fecha(o.fechaPlan)}</td>
                        <td className="cod">{o.salaCodigo}</td>
                        <td>
                          <EstadoOt estado={o.estado} />
                          {o.sustituida && (
                            <span className="etiqueta info" style={{ marginLeft: 6 }}>
                              SUSTITUIDA
                            </span>
                          )}
                        </td>
                        <td className="suave pequena">
                          <EstadoOrigen origen={o.origen} />
                        </td>
                        <td className="suave pequena">
                          {o.planCodigo === null ? '—' : `${o.planCodigo} v${String(o.planVersion)}`}
                        </td>
                        <td className="num">
                          {o.emision > 1 ? (
                            <span className="etiqueta info">{o.emision}</span>
                          ) : (
                            <span className="tenue">1</span>
                          )}
                        </td>
                        <td className="num">{o.lineas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="lista-ots-movil">
                {(estado.datos?.filas ?? []).map((o) => (
                  <TarjetaOtContextual key={o.id} ot={o} />
                ))}
              </ul>
            </>
          )}
        </div>

        {totalPaginas > 1 && (
          <div className="panel-cuerpo fila separa">
            <span className="suave pequena">
              Página {pagina} de {totalPaginas}
            </span>
            <div className="fila">
              <button
                className="pequeno"
                disabled={pagina <= 1}
                onClick={() => lista.fijar('pagina', String(pagina - 1))}
              >
                Anterior
              </button>
              <button
                className="pequeno"
                disabled={pagina >= totalPaginas}
                onClick={() => lista.fijar('pagina', String(pagina + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// --------------------------------------------------------- listado global

/**
 * Listado GLOBAL de OT (FASE 3.3-A): mismo patrón de filtros-en-la-URL y
 * paginación que `PaginaOts` (por centro), pero sin el contexto de
 * `useCentro()` — Cliente y Centro pasan a ser filtros más, no la ruta.
 *
 * Centro depende de Cliente: hasta que no se elige un cliente no se pide
 * `GET /api/clientes/:id/centros` (una sola llamada, acotada), en vez de
 * agregar los centros de todos los clientes — eso sí sería infraestructura
 * nueva, y no hace falta para este primer incremento.
 */
type OrdenOtsGlobal = 'codigo' | 'fechaPlan' | 'cliente' | 'centro' | 'estado';

/**
 * `fechaPlan DESC`, igual que en el listado por centro: es el orden que
 * ya aplicaba el backend, y con estos valores por defecto `orderBy`/
 * `orderDir` no se mandan, así que el backend sigue tomando la rama que
 * preserva ese orden literal.
 */
const DEFAULTS_OTS_GLOBAL = {
  q: '', estado: '', fechaDesde: '', fechaHasta: '', idCliente: '', idCentro: '',
  orden: 'fechaPlan', dir: 'desc', pagina: '1',
} as const;

export function PaginaOtsGlobal(): ReactNode {
  const navegar = useNavigate();
  const { puede } = useSesion();
  const conectividad = useConectividadAlEntrar();
  const lista = useListaUrl(DEFAULTS_OTS_GLOBAL, 'pagina');
  //  FASE 4.3-E: selección de OT completas (no líneas) para revisar y
  //  confirmar sus líneas —con ejecutores por línea, nunca uno común
  //  para todas— en `RevisionMultiOt`. Sin memoria entre páginas de
  //  filtro/paginación, mismo criterio que "seleccionar elegibles" de
  //  las líneas: lo que no está cargado ahora mismo no se puede marcar.
  const puedeConfirmarMasivo = puede('ot_lineas:confirmar');
  const puedeVerificarMasivo = puede('ot_lineas:verificar');
  //  La columna de selección se muestra si el usuario puede ejecutar
  //  CUALQUIERA de las dos acciones de lote — cada botón, más abajo, se
  //  gatea por su propio permiso.
  const puedeSeleccionarMasivo = puedeConfirmarMasivo || puedeVerificarMasivo;
  const [seleccionOts, setSeleccionOts] = useState<ReadonlySet<string>>(new Set());
  const [revisando, setRevisando] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [creandoOt, setCreandoOt] = useState(false);

  function alternarOt(id: string): void {
    setSeleccionOts((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }

  const q = lista.valor('q');
  const filtroEstado = lista.valor('estado');
  const fechaDesde = lista.valor('fechaDesde');
  const fechaHasta = lista.valor('fechaHasta');
  const idCliente = lista.valor('idCliente');
  const idCentro = lista.valor('idCentro');
  const orden = lista.valor('orden') as OrdenOtsGlobal;
  const dir = lista.valor('dir') as Dir;
  const pagina = Number(lista.valor('pagina'));

  function ordenarPor(columna: OrdenOtsGlobal): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  OT-UX-DEBT-01 (DEBT-ZERO-01): mismo criterio que en `PaginaOts` —
  //  en móvil, sin orden explícito, por código DESC.
  const esMovil = useEsMovil();
  const usaOrdenPorDefecto = orden === DEFAULTS_OTS_GLOBAL.orden && dir === DEFAULTS_OTS_GLOBAL.dir;
  const ordenEfectivo: OrdenOtsGlobal = esMovil && usaOrdenPorDefecto ? 'codigo' : orden;
  const dirEfectivo: Dir = esMovil && usaOrdenPorDefecto ? 'desc' : dir;

  function elegirCliente(valor: string): void {
    //  Cambiar de cliente invalida el centro elegido (era de otro
    //  cliente, o de ninguno): se fija en la MISMA actualización para no
    //  dejar un instante con cliente y centro incoherentes entre sí.
    lista.fijarVarios({ idCliente: valor, idCentro: DEFAULTS_OTS_GLOBAL.idCentro });
  }

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`/`orden`/`dir`/`pagina`.
  const contadorFiltros = [
    filtroEstado !== DEFAULTS_OTS_GLOBAL.estado,
    fechaDesde !== DEFAULTS_OTS_GLOBAL.fechaDesde,
    fechaHasta !== DEFAULTS_OTS_GLOBAL.fechaHasta,
    idCliente !== DEFAULTS_OTS_GLOBAL.idCliente,
    idCentro !== DEFAULTS_OTS_GLOBAL.idCentro,
  ].filter(Boolean).length;

  const clientes = useDatos(() => api.get<{ clientes: Cliente[] }>('/api/clientes'), []);
  const centrosDelCliente = useDatos(
    () =>
      idCliente === ''
        ? Promise.resolve({ centros: [] as Centro[] })
        : api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`),
    [idCliente],
  );

  const consulta = new URLSearchParams();
  if (q.trim() !== '') consulta.set('q', q.trim());
  if (filtroEstado !== '') consulta.set('estado', filtroEstado);
  if (fechaDesde !== '') consulta.set('fechaDesde', fechaDesde);
  if (fechaHasta !== '') consulta.set('fechaHasta', fechaHasta);
  if (idCliente !== '') consulta.set('idCliente', idCliente);
  if (idCentro !== '') consulta.set('idCentro', idCentro);
  //  `orderBy`/`orderDir` van juntos o no van: ver el mismo comentario en
  //  `PaginaOts` — omitir sólo uno de los dos porque coincide con el
  //  default dejaría al otro suelto, y el backend aplicaría su propio
  //  valor seguro (ASC) en vez del `desc` que de verdad se pidió. Se manda
  //  el EFECTIVO — ver el comentario de `ordenEfectivo` más arriba.
  if (ordenEfectivo !== DEFAULTS_OTS_GLOBAL.orden || dirEfectivo !== DEFAULTS_OTS_GLOBAL.dir) {
    consulta.set('orderBy', ordenEfectivo);
    consulta.set('orderDir', dirEfectivo);
  }
  consulta.set('pagina', String(pagina));
  consulta.set('porPagina', '50');

  const estado = useDatos(
    () => api.get<PaginaOtGlobal>(`/api/ots?${consulta.toString()}`),
    [consulta.toString()],
  );

  const totalPaginas = Math.max(1, Math.ceil((estado.datos?.total ?? 0) / 50));

  if (conectividad === 'OFFLINE') {
    return <Navigate to="/offline" replace state={{ avisoOffline: MENSAJE_OFFLINE_OTS }} />;
  }

  return (
    //  UX-1B: un solo título (antes h1 y un h2 idéntico en el panel); «Nueva OT» como acción de la página.
    <Shell titulo="Órdenes de trabajo"
      meta={<span>{estado.datos?.total ?? 0} en total</span>}
      acciones={puede('ots:crear_manual') && !creandoOt ? <button className="principal" onClick={() => setCreandoOt(true)}>Nueva OT</button> : undefined}>
      {creandoOt && (
        <FormularioNuevaOt
          centroFijo={null}
          alCancelar={() => setCreandoOt(false)}
          alCrear={(ot) => navegar(`/ots/${ot.id}`)}
        />
      )}

      <div className="panel">

        <FiltrosResponsive
          contador={contadorFiltros}
          busqueda={
            <div className="campo campo-busqueda">
              <label htmlFor="ots-q">Buscar</label>
              <input
                id="ots-q"
                type="text"
                placeholder="Código de OT, cliente, centro o plan…"
                value={q}
                onChange={(e) => lista.fijar('q', e.target.value)}
              />
            </div>
          }
        >
          <div className="campo">
            <label htmlFor="ots-estado">Estado</label>
            <select
              id="ots-estado"
              value={filtroEstado}
              onChange={(e) => lista.fijar('estado', e.target.value)}
            >
              <option value="">Todos</option>
              {ESTADOS.map((es) => (
                <option key={es} value={es}>
                  {etiquetaEstadoOt(es)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="ots-desde">Desde</label>
            <input
              id="ots-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => lista.fijar('fechaDesde', e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="ots-hasta">Hasta</label>
            <input
              id="ots-hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => lista.fijar('fechaHasta', e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="ots-cliente">Cliente</label>
            <select
              id="ots-cliente"
              value={idCliente}
              onChange={(e) => elegirCliente(e.target.value)}
            >
              <option value="">Todos</option>
              {(clientes.datos?.clientes ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razonSocial}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="ots-centro">Centro</label>
            <select
              id="ots-centro"
              value={idCentro}
              disabled={idCliente === ''}
              onChange={(e) => lista.fijar('idCentro', e.target.value)}
            >
              <option value="">{idCliente === '' ? 'Elige un cliente primero' : 'Todos'}</option>
              {(centrosDelCliente.datos?.centros ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.descripcion}
                </option>
              ))}
            </select>
          </div>
          {!lista.esPorDefecto && (
            <button className="pequeno" onClick={lista.limpiar}>
              Limpiar filtros
            </button>
          )}
        </FiltrosResponsive>

        <div className="panel-cuerpo sin-relleno">
          {estado.cargando && estado.datos === null ? (
            <Cargando />
          ) : estado.error !== null ? (
            <ErrorCaja error={estado.error} reintentar={estado.recargar} />
          ) : (estado.datos?.filas.length ?? 0) === 0 ? (
            //  UX-1C: vacío real ≠ vacío por filtros.
            lista.esPorDefecto
              ? <EmptyState titulo="No hay órdenes de trabajo" descripcion="Las OT emitidas por el plan o creadas a mano aparecerán aquí." />
              : <SinResultados />
          ) : (
            <>
              {(puedeConfirmarMasivo || puedeVerificarMasivo) && (
                <div className="barra-bulk">
                  <div className="fila barra-bulk-fila">
                    <button
                      type="button"
                      className="pequeno"
                      onClick={() => setSeleccionOts(new Set((estado.datos?.filas ?? []).map((o) => o.id)))}
                    >
                      Seleccionar todas las visibles
                    </button>
                    <button
                      type="button"
                      className="pequeno"
                      onClick={() => setSeleccionOts(new Set())}
                      disabled={seleccionOts.size === 0}
                    >
                      Limpiar selección
                    </button>
                    <span className="pequena suave barra-bulk-contador">
                      {seleccionOts.size} seleccionada{seleccionOts.size === 1 ? '' : 's'}
                    </span>
                    <span className="barra-bulk-relleno" />
                    {puedeVerificarMasivo && (
                      <button
                        type="button"
                        //  UX-POLISH-01 punto 6: mismo tratamiento que
                        //  "Verificar seleccionadas" dentro de una OT
                        //  (BarraSeleccionMasiva, más abajo) — verde
                        //  `.exito`, nunca un botón sin clase. Es el mismo
                        //  botón de acción (`CLASE_ACCION.verificar`), no
                        //  dos jerarquías distintas para la misma cosa.
                        className="exito"
                        disabled={seleccionOts.size === 0}
                        onClick={() => setVerificando(true)}
                      >
                        Verificar seleccionadas
                      </button>
                    )}
                    {puedeConfirmarMasivo && (
                      <button
                        type="button"
                        className="principal"
                        disabled={seleccionOts.size === 0}
                        onClick={() => setRevisando(true)}
                      >
                        Confirmar líneas seleccionadas
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/*  FASE 3.3-D4: misma nota que en `PaginaOts` — mismo array,
               *  dos representaciones, la oculta con `display: none`. */}
              <div className="tabla-desliza tabla-ots-desktop">
                <table>
                  <thead>
                    <tr>
                      {puedeSeleccionarMasivo && <th className="col-check" aria-label="Seleccionar" />}
                      <CabeceraOrdenable
                        texto="OT"
                        columna="codigo"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('codigo')}
                      />
                      <CabeceraOrdenable
                        texto="Fecha"
                        columna="fechaPlan"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('fechaPlan')}
                      />
                      <CabeceraOrdenable
                        texto="Cliente"
                        columna="cliente"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('cliente')}
                      />
                      <CabeceraOrdenable
                        texto="Centro"
                        columna="centro"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('centro')}
                      />
                      <th>Plan</th>
                      <CabeceraOrdenable
                        texto="Estado"
                        columna="estado"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('estado')}
                      />
                      <th className="num">Emisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(estado.datos?.filas ?? []).map((o) => (
                      <tr key={o.id} className="pulsable" onClick={() => navegar(`/ots/${o.id}`)}>
                        {puedeSeleccionarMasivo && (
                          <td className="col-check" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={seleccionOts.has(o.id)}
                              onChange={() => alternarOt(o.id)}
                              aria-label={`Seleccionar ${o.codigo}`}
                            />
                          </td>
                        )}
                        <td className="cod">{o.codigo}</td>
                        <td>{fecha(o.fechaPlan)}</td>
                        <td>{o.clienteRazonSocial}</td>
                        <td>
                          <span className="texto-principal">{o.centroDescripcion}</span>{' '}
                          <span className="cod suave">{o.centroCodigo}</span>
                        </td>
                        <td className="suave pequena">
                          {o.planCodigo === null ? '—' : `${o.planCodigo} v${String(o.planVersion)}`}
                        </td>
                        <td>
                          <EstadoOt estado={o.estado} />
                          {o.sustituida && (
                            <span className="etiqueta info" style={{ marginLeft: 6 }}>
                              SUSTITUIDA
                            </span>
                          )}
                        </td>
                        <td className="num">
                          {o.emision > 1 ? (
                            <span className="etiqueta info">{o.emision}</span>
                          ) : (
                            <span className="tenue">1</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="lista-ots-movil">
                {(estado.datos?.filas ?? []).map((o) => (
                  <TarjetaOtGlobal
                    key={o.id}
                    ot={o}
                    seleccionable={puedeSeleccionarMasivo}
                    seleccionada={seleccionOts.has(o.id)}
                    onSeleccionar={() => alternarOt(o.id)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>

        {totalPaginas > 1 && (
          <div className="panel-cuerpo fila separa">
            <span className="suave pequena">
              Página {pagina} de {totalPaginas}
            </span>
            <div className="fila">
              <button
                className="pequeno"
                disabled={pagina <= 1}
                onClick={() => lista.fijar('pagina', String(pagina - 1))}
              >
                Anterior
              </button>
              <button
                className="pequeno"
                disabled={pagina >= totalPaginas}
                onClick={() => lista.fijar('pagina', String(pagina + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {revisando && (
        <RevisionMultiOt
          idsOt={[...seleccionOts]}
          cerrar={() => setRevisando(false)}
          alTerminar={() => estado.recargar()}
        />
      )}
      {verificando && (
        <VerificacionMultiOt
          idsOt={[...seleccionOts]}
          cerrar={() => setVerificando(false)}
          alTerminar={() => estado.recargar()}
        />
      )}
    </Shell>
  );
}

// ------------------------------------------------------------ generación

/**
 * Familias (planes lógicos) disponibles en un centro, para el selector de
 * "Generar próximas OTs"/"Basar en plan" — PLAN-UX-01-C, puntos 7-8. Nunca
 * una versión: el motor resuelve sola la versión aplicable a cada fecha.
 * Sólo planes publicados (nunca BORRADOR: no hay nada estable que ofrecer).
 */
function familiasDePlan(planes: readonly Plan[]): { codigo: string; descripcion: string }[] {
  const m = new Map<string, string>();
  for (const p of planes) {
    if (p.estado === 'BORRADOR') continue;
    if (!m.has(p.codigo)) m.set(p.codigo, p.descripcion);
  }
  return [...m.entries()].map(([codigo, descripcion]) => ({ codigo, descripcion })).sort((a, b) => a.codigo.localeCompare(b.codigo));
}

interface ResultadoGeneracion {
  aplicado: boolean;
  fechaDesde: string;
  fechaHasta: string;
  semanas: number;
  otsCreadas: number;
  lineasCreadas: number;
  ocurrenciasCreadas: number;
  /** PLAN-UX-01-C: cuántas OT resultarían — válido en preview (donde `otsCreadas` es siempre 0) y en real. */
  otsPropuestas: number;
  /** PLAN-UX-01-C: versiones que de verdad intervienen en el resultado. */
  versionesIntervinientes: { codigo: string; version: number; vigenteDesde: string; vigenteHasta: string | null }[];
  otsExistentes: number;
  otsExistentesAnuladas: number;
  otsExistentesCerradas: number;
  ocurrenciasYaCubiertas: number;
  ocurrenciasDesplazadas: number;
  ocurrenciasAbsorbidas: number;
  ocurrenciasSaltadas: number;
  ocurrenciasSinDiaOperativo: number;
  conflictosVersion: { codigoPlan: string; codigoSala: string; fecha: string; codigoOtExistente: string }[];
  avisos: { codigo: string; mensaje: string }[];
}

/**
 * Generar es una acción interna explícita, no un job.
 *
 * Antes de escribir nada se enseña qué se va a hacer y sobre qué ventana.
 * La previsualización usa el MISMO cálculo que la aplicación —el servicio
 * corre en READ ONLY— así que lo que se ve es lo que pasa.
 */
export function GenerarOts({
  idCentro,
  codigoPlanFijo,
  alCerrar,
}: {
  idCentro: string;
  /** No nulo = abierta desde la ficha del Plan: la familia viene preseleccionada y fija (punto 10). */
  codigoPlanFijo?: string;
  alCerrar: () => void;
}): ReactNode {
  const planesDelCentro = useDatos(() => api.get<{ planes: Plan[] }>(`/api/centros/${idCentro}/planes`), [idCentro]);
  const familias = useMemo(() => familiasDePlan(planesDelCentro.datos?.planes ?? []), [planesDelCentro.datos]);
  const [codigoPlan, setCodigoPlan] = useState(codigoPlanFijo ?? '');
  const [fechaDesde, setFechaDesde] = useState(hoy());
  const [semanas, setSemanas] = useState(8);
  const [previa, setPrevia] = useState<ResultadoGeneracion | null>(null);
  const [resultado, setResultado] = useState<ResultadoGeneracion | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);

  async function llamar(soloPrevisualizar: boolean): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      const r = await api.post<ResultadoGeneracion>(`/api/centros/${idCentro}/generar-ots`, {
        fechaDesde,
        semanas,
        soloPrevisualizar,
        codigoPlan,
      });
      if (soloPrevisualizar) setPrevia(r);
      else setResultado(r);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  if (resultado !== null) {
    return (
      <div className="panel">
        <div className="panel-cabecera">
          <h2>Generación terminada</h2>
          <button className="pequeno" onClick={alCerrar}>
            Cerrar
          </button>
        </div>
        <div className="panel-cuerpo">
          <div className="nota exito">
            <strong>
              {resultado.otsCreadas} órdenes creadas para {fecha(resultado.fechaDesde)} →{' '}
              {fecha(resultado.fechaHasta)}.
            </strong>
          </div>
          <ResumenGeneracion r={resultado} />
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Generar órdenes de trabajo</h2>
        <button className="pequeno" onClick={alCerrar}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}

        <div className="fila" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="campo" style={{ minWidth: 220, margin: 0 }}>
            <label htmlFor="g-plan">Plan</label>
            <select
              id="g-plan"
              value={codigoPlan}
              disabled={codigoPlanFijo !== undefined}
              onChange={(e) => {
                setCodigoPlan(e.target.value);
                setPrevia(null);
              }}
            >
              <option value="">Elige un plan…</option>
              {familias.map((f) => (
                <option key={f.codigo} value={f.codigo}>
                  {f.descripcion} ({f.codigo})
                </option>
              ))}
            </select>
          </div>
          <div className="campo" style={{ maxWidth: 180, margin: 0 }}>
            <label htmlFor="g-desde">Desde</label>
            <input
              id="g-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                setPrevia(null);
              }}
            />
          </div>
          <div className="campo" style={{ maxWidth: 130, margin: 0 }}>
            <label htmlFor="g-semanas">Horizonte (semanas)</label>
            <input
              id="g-semanas"
              type="number"
              min={1}
              max={52}
              value={semanas}
              onChange={(e) => {
                setSemanas(Number(e.target.value));
                setPrevia(null);
              }}
            />
          </div>
          <button
            disabled={ocupado || codigoPlan === ''}
            onClick={() => {
              void llamar(true);
            }}
          >
            {ocupado ? 'Calculando…' : 'Ver qué se generaría'}
          </button>
        </div>

        {previa !== null && (
          <>
            <div className="nota" style={{ marginTop: 16 }}>
              Periodo <strong>{fecha(previa.fechaDesde)}</strong> →{' '}
              <strong>{fecha(previa.fechaHasta)}</strong> · {previa.semanas} semanas. Todavía no se
              ha escrito nada.
            </div>
            <ResumenGeneracion r={previa} />
            <button
              className="principal sep"
              disabled={ocupado || previa.otsPropuestas === 0}
              onClick={() => {
                void llamar(false);
              }}
            >
              {ocupado ? 'Generando…' : `Generar ${String(previa.otsPropuestas)} OT${previa.otsPropuestas === 1 ? '' : 's'}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ResumenGeneracion({ r }: { r: ResultadoGeneracion }): ReactNode {
  return (
    <>
      {r.versionesIntervinientes.length > 0 && (
        <div className="nota sep">
          <strong>Versiones que intervienen</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {r.versionesIntervinientes.map((v) => (
              <li key={`${v.codigo}-v${String(v.version)}`}>
                {v.codigo} v{v.version} — {fecha(v.vigenteDesde)} → {v.vigenteHasta === null ? 'sin fin' : fecha(v.vigenteHasta)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="cifras sep">
        <Cifra n={r.aplicado ? r.otsCreadas : r.otsPropuestas} t={r.aplicado ? 'OT creadas' : 'OT que se crearían'} />
        <Cifra n={r.lineasCreadas} t="Líneas creadas" />
        <Cifra n={r.ocurrenciasCreadas} t="Ocurrencias" />
        <Cifra n={r.otsExistentes} t="Ya existían" />
        <Cifra n={r.ocurrenciasYaCubiertas} t="Ya cubiertas" />
        <Cifra n={r.ocurrenciasDesplazadas} t="Desplazadas" />
        <Cifra n={r.ocurrenciasAbsorbidas} t="Absorbidas" />
        <Cifra n={r.ocurrenciasSaltadas} t="Saltadas" />
      </div>

      {r.conflictosVersion.length > 0 && (
        <div className="nota aviso sep">
          <strong>Colisiones entre versiones</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {r.conflictosVersion.map((c, i) => (
              <li key={i}>
                {c.codigoSala} · {fecha(c.fecha)} — ya existe {c.codigoOtExistente} de otra versión
                de {c.codigoPlan}
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.avisos.length > 0 && (
        <details className="sep">
          <summary className="suave pequena" style={{ cursor: 'pointer' }}>
            {r.avisos.length} aviso(s)
          </summary>
          <ul className="pequena suave" style={{ marginTop: 6, paddingLeft: 18 }}>
            {r.avisos.map((a, i) => (
              <li key={i}>{a.mensaje}</li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

/** Hoy LOCAL, coherente con `horaAhora()` (antes UTC: ver `hoyLocal`, UAT 9.6-B1-B). */
function hoy(): string {
  return hoyLocal();
}

function horaAhora(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// --------------------------------------------------- OT manual (FUNC-OT-01)
//
// UAT FASE 4.3: hasta ahora una OT sólo nacía del generador, a partir de
// un plan. Esta es la otra puerta — ya prevista por el esquema desde 0000
// (`origen` admite PUNTUAL/EXTRA/CORRECTIVA, `id_plan` es NULLABLE) pero
// nunca usada por ningún código hasta ahora. Ver el porqué de PUNTUAL (y
// no EXTRA/CORRECTIVA) en `backend/src/ots-manual/tipos.ts`.
//
// Una vez creada, esta OT es indistinguible operativamente de cualquier
// otra: entra en la MISMA máquina de estados, admite la MISMA asignación
// y ejecución. Este formulario sólo decide cómo nace.

interface LineaNuevaOt {
  readonly clave: string;
  idUbicacion: string;
  idTipo: string;
  observaciones: string;
  /** PLAN-UX-01-C: tarea del plan de la que esta línea viene, si alguna. El servidor decide si de verdad corresponde. */
  idPlanLinea: string | null;
}

function nuevaLineaVacia(): LineaNuevaOt {
  return { clave: crypto.randomUUID(), idUbicacion: '', idTipo: '', observaciones: '', idPlanLinea: null };
}

function lineaDesdeTarea(t: TareaPropuestaOt): LineaNuevaOt {
  return {
    clave: crypto.randomUUID(),
    idUbicacion: t.idUbicacion,
    idTipo: t.idTipo,
    observaciones: '',
    idPlanLinea: t.idPlanLinea,
  };
}

/** ¿`id` cuelga de `idSala` (la propia sala incluida)? Recorre `idPadre` hacia arriba. */
function bajoSala(porId: Map<string, Ubicacion>, idSala: string, id: string): boolean {
  let actual: string | null = id;
  let saltos = 0;
  while (actual !== null && saltos < 50) {
    if (actual === idSala) return true;
    actual = porId.get(actual)?.idPadre ?? null;
    saltos += 1;
  }
  return false;
}

function rutaLegible(porId: Map<string, Ubicacion>, id: string): string {
  const partes: string[] = [];
  let actual: string | null = id;
  let saltos = 0;
  while (actual !== null && saltos < 50) {
    const u = porId.get(actual);
    if (u === undefined) break;
    partes.unshift(u.descripcion);
    actual = u.idPadre;
    saltos += 1;
  }
  return partes.join(' > ');
}

const LIMITE_RESULTADOS_UBICACION = 30;

/**
 * Buscador de ubicaciones acotado a las que cuelgan de una sala — mismo
 * criterio transversal de selección de entidades (buscar + nombre + código
 * + contexto) que `SelectorCentros` (`paginas/operarios.tsx`), replicado
 * aquí porque ese componente es local a esa página y el tipo de dato
 * cambia (ubicación, no centro).
 */
function SelectorUbicacionEnSala({
  ubicaciones,
  idSala,
  valor,
  onSeleccionar,
}: {
  ubicaciones: Ubicacion[];
  idSala: string;
  valor: string;
  onSeleccionar: (id: string) => void;
}): ReactNode {
  const [busqueda, setBusqueda] = useState('');
  const [editando, setEditando] = useState(valor === '');
  const porId = new Map(ubicaciones.map((u) => [u.id, u]));
  const seleccionada = porId.get(valor) ?? null;

  if (!editando && seleccionada !== null) {
    return (
      <div className="fila" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          <strong>{seleccionada.codigo}</strong>{' '}
          <span className="suave pequena">{rutaLegible(porId, seleccionada.id)}</span>
        </span>
        <button type="button" className="pequeno" onClick={() => setEditando(true)}>
          Cambiar
        </button>
      </div>
    );
  }

  const candidatas = ubicaciones.filter((u) => u.activo && bajoSala(porId, idSala, u.id));
  const t = busqueda.trim().toLowerCase();
  const filtradas =
    t === ''
      ? candidatas
      : candidatas.filter((u) => u.codigo.toLowerCase().includes(t) || u.descripcion.toLowerCase().includes(t));
  const mostradas = filtradas.slice(0, LIMITE_RESULTADOS_UBICACION);

  return (
    <div className="selector-centros">
      <input
        type="text"
        placeholder="Nombre o código de ubicación…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        autoFocus
      />
      {candidatas.length === 0 ? (
        <p className="suave pequena">Esta sala no tiene ubicaciones activas bajo ella.</p>
      ) : mostradas.length === 0 ? (
        <p className="suave pequena">Sin resultados para «{busqueda}».</p>
      ) : (
        <ul className="lista-resultados">
          {mostradas.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className="resultado-centro"
                onClick={() => {
                  onSeleccionar(u.id);
                  setBusqueda('');
                  setEditando(false);
                }}
              >
                <span className="resultado-texto">
                  <strong>
                    {u.descripcion} <span className="mono suave">{u.codigo}</span>
                  </strong>
                  <span className="suave">{rutaLegible(porId, u.id)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {filtradas.length > LIMITE_RESULTADOS_UBICACION && (
        <p className="tenue pequena" style={{ marginTop: 6, marginBottom: 0 }}>
          Mostrando {LIMITE_RESULTADOS_UBICACION} de {filtradas.length} resultados. Afina la búsqueda.
        </p>
      )}
    </div>
  );
}

/**
 * «Nueva OT» — FUNC-OT-01. `centroFijo` no nulo = abierta desde la ficha
 * de un centro (el centro ya no se elige aquí); nulo = abierta desde el
 * listado global, con el mismo selector cliente→centro que ya usan los
 * filtros de esta misma pantalla (`PaginaOtsGlobal`).
 */
export function FormularioNuevaOt({
  centroFijo,
  codigoPlanFijo,
  idSalaInicial,
  fechaInicial,
  horaInicial,
  alCancelar,
  alCrear,
}: {
  centroFijo: Centro | null;
  /** No nulo = abierta desde la ficha del Plan: la familia viene preseleccionada y fija (punto 10). */
  codigoPlanFijo?: string;
  /**
   * FASE 6.7-A §M: clic en un hueco del Planner — mismo formulario de
   * siempre, sólo con centro/sala/fecha/hora ya elegidos por dónde se hizo
   * clic. Nunca un INSERT directo desde el Planner; esto sigue siendo la
   * única vía de creación manual.
   */
  idSalaInicial?: string;
  fechaInicial?: string;
  horaInicial?: string;
  alCancelar: () => void;
  alCrear: (ot: OtManualCreada) => void;
}): ReactNode {
  const [idCliente, setIdCliente] = useState(centroFijo?.idCliente ?? '');
  const [centro, setCentro] = useState<Centro | null>(centroFijo);
  const [idSala, setIdSala] = useState(idSalaInicial ?? '');
  const [fechaValor, setFechaValor] = useState(fechaInicial ?? hoy());
  const [horaValor, setHoraValor] = useState(horaInicial ?? horaAhora());
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState<LineaNuevaOt[]>([nuevaLineaVacia()]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  //  «Basar en plan» — PLAN-UX-01-C.
  const [codigoPlan, setCodigoPlan] = useState(codigoPlanFijo ?? '');
  const [propuesta, setPropuesta] = useState<PropuestaTareasOt | null>(null);
  const [fechaCargada, setFechaCargada] = useState<string | null>(null);
  const [cargandoPropuesta, setCargandoPropuesta] = useState(false);
  const [errorPropuesta, setErrorPropuesta] = useState<unknown>(null);
  const [opcionesSN, setOpcionesSN] = useState<{ tarea: TareaPropuestaOt; incluida: boolean }[]>([]);
  const [confirmarParcial, setConfirmarParcial] = useState(false);

  const planesDelCentro = useDatos(
    () => (centro === null ? Promise.resolve({ planes: [] as Plan[] }) : api.get<{ planes: Plan[] }>(`/api/centros/${centro.id}/planes`)),
    [centro?.id],
  );
  const familias = useMemo(() => familiasDePlan(planesDelCentro.datos?.planes ?? []), [planesDelCentro.datos]);

  const clientes = useDatos(
    () => (centroFijo !== null ? Promise.resolve({ clientes: [] as Cliente[] }) : api.get<{ clientes: Cliente[] }>('/api/clientes')),
    [centroFijo],
  );
  const centrosDelCliente = useDatos(
    () =>
      centroFijo !== null || idCliente === ''
        ? Promise.resolve({ centros: [] as Centro[] })
        : api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`),
    [centroFijo, idCliente],
  );
  const ubicaciones = useDatos(
    () =>
      centro === null
        ? Promise.resolve({ ubicaciones: [] as Ubicacion[] })
        : api.get<{ ubicaciones: Ubicacion[] }>(`/api/centros/${centro.id}/ubicaciones`),
    [centro?.id],
  );
  const tipos = useDatos(
    () =>
      centro === null
        ? Promise.resolve({ tiposLimpieza: [] as TipoLimpieza[] })
        : api.get<{ tiposLimpieza: TipoLimpieza[] }>(`/api/clientes/${centro.idCliente}/tipos-limpieza`),
    [centro?.idCliente],
  );

  const salas = (ubicaciones.datos?.ubicaciones ?? []).filter((u) => u.tipo === 'SALA' && u.activo);
  const tiposActivos = (tipos.datos?.tiposLimpieza ?? []).filter((t) => t.activo);

  function elegirCliente(id: string): void {
    setIdCliente(id);
    setCentro(null);
    setIdSala('');
  }
  function elegirCentro(id: string): void {
    const c = (centrosDelCliente.datos?.centros ?? []).find((x) => x.id === id) ?? null;
    setCentro(c);
    setIdSala('');
  }

  function actualizarLinea(clave: string, cambios: Partial<LineaNuevaOt>): void {
    setLineas((prev) => prev.map((l) => (l.clave === clave ? { ...l, ...cambios } : l)));
  }
  function quitarLinea(clave: string): void {
    setLineas((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.clave !== clave)));
  }

  //  C2: contador de la petición de propuesta EN VUELO — para que una
  //  respuesta vieja (p.ej. tras cambiar plan/sala/fecha varias veces
  //  seguidas, con la red devolviendo fuera de orden) nunca pueda
  //  sobrescribir un estado más reciente. `limpiarPropuesta` también lo
  //  incrementa: invalida cualquier petición que siguiera en vuelo.
  const peticionPropuestaRef = useRef(0);

  /**
   * Toda tarea propuesta por un plan está atada a LA SALA con la que se
   * pidió — igual que cualquier ubicación elegida a mano está atada a la
   * sala del formulario (`SelectorUbicacionEnSala` sólo ofrece ubicaciones
   * bajo `idSala`; el backend lo vuelve a exigir con
   * `UBICACION_FUERA_DE_SALA`). Cambiar plan O sala invalida por igual lo
   * que hubiera cargado o escrito antes: no hay automatismo que intente
   * adivinar qué seguía siendo válido, se limpia y se fuerza a recargar o
   * a volver a elegir. Nunca toca `fechaValor`/`horaValor`/`observaciones`.
   */
  function limpiarPropuesta(): void {
    peticionPropuestaRef.current += 1;
    setPropuesta(null);
    setFechaCargada(null);
    setOpcionesSN([]);
    setConfirmarParcial(false);
    setErrorPropuesta(null);
    setLineas([nuevaLineaVacia()]);
  }

  //  «Basar en plan» (PLAN-UX-01-C). Recarga SIEMPRE sustituye la propuesta
  //  anterior — es una acción explícita del usuario (botón, nunca un efecto
  //  automático que se dispare solo), así que sustituir es lo esperado.
  async function cargarPropuesta(): Promise<void> {
    if (centro === null || codigoPlan === '' || idSala === '' || fechaValor === '') return;
    const peticion = (peticionPropuestaRef.current += 1);
    setCargandoPropuesta(true);
    setErrorPropuesta(null);
    try {
      const params = new URLSearchParams({ codigoPlan, idSala, fecha: fechaValor });
      const r = await api.get<PropuestaTareasOt>(`/api/centros/${centro.id}/tareas-para-fecha?${params.toString()}`);
      //  C2 (letra B/G): si entretanto se lanzó otra petición más reciente
      //  (o un cambio de plan/sala la invalidó vía `limpiarPropuesta`), esta
      //  respuesta ya está vieja — no pisar el estado actual con ella.
      if (peticionPropuestaRef.current !== peticion) return;
      setPropuesta(r);
      setFechaCargada(fechaValor);
      setLineas(r.tareas.filter((t) => !t.segunNecesidad).map(lineaDesdeTarea));
      setOpcionesSN(r.tareas.filter((t) => t.segunNecesidad).map((t) => ({ tarea: t, incluida: false })));
      setConfirmarParcial(false);
    } catch (e) {
      if (peticionPropuestaRef.current !== peticion) return;
      setErrorPropuesta(e);
    } finally {
      if (peticionPropuestaRef.current === peticion) setCargandoPropuesta(false);
    }
  }

  function alternarSN(idPlanLinea: string): void {
    const opcion = opcionesSN.find((o) => o.tarea.idPlanLinea === idPlanLinea);
    if (opcion === undefined) return;
    setOpcionesSN((prev) => prev.map((o) => (o.tarea.idPlanLinea === idPlanLinea ? { ...o, incluida: !o.incluida } : o)));
    setLineas((prev) =>
      opcion.incluida ? prev.filter((l) => l.idPlanLinea !== idPlanLinea) : [...prev, lineaDesdeTarea(opcion.tarea)],
    );
  }

  const lineasCompletas = lineas.filter((l) => l.idUbicacion !== '' && l.idTipo !== '');
  const puedeEnviar =
    centro !== null && idSala !== '' && fechaValor !== '' && horaValor !== '' && lineasCompletas.length === lineas.length;

  //  Cobertura (punto 3): de las tareas RECURRENTES que el plan proponía
  //  para esta fecha/sala, cuántas siguen de verdad en `lineas` — SEGÚN
  //  NECESIDAD no cuenta, porque nunca se propone incluida por defecto.
  const recurrentesPropuestas = propuesta === null ? [] : propuesta.tareas.filter((t) => !t.segunNecesidad);
  const idsPlanLineaIncluidos = new Set(lineas.map((l) => l.idPlanLinea).filter((x): x is string => x !== null));
  const recurrentesCubiertas = recurrentesPropuestas.filter((t) => idsPlanLineaIncluidos.has(t.idPlanLinea));
  const hayCoberturaParcial = propuesta !== null && recurrentesCubiertas.length < recurrentesPropuestas.length;

  /** Añade las que faltan y envía con la lista completa — no con el estado, que todavía no se habría repintado. */
  function incluirTodasYEnviar(): void {
    const faltantes = recurrentesPropuestas.filter((t) => !idsPlanLineaIncluidos.has(t.idPlanLinea));
    const completas = [...lineas, ...faltantes.map(lineaDesdeTarea)];
    setLineas(completas);
    setConfirmarParcial(false);
    void enviar(completas);
  }

  async function enviar(lineasAEnviar: LineaNuevaOt[] = lineas): Promise<void> {
    if (centro === null) return;
    setEnviando(true);
    setError(null);
    try {
      const ot = await api.post<OtManualCreada>('/api/ots', {
        idCentro: centro.id,
        idSala,
        fecha: fechaValor,
        hora: horaValor,
        observaciones: observaciones.trim() === '' ? null : observaciones.trim(),
        lineas: lineasAEnviar.map((l) => ({
          idUbicacion: l.idUbicacion,
          idTipo: l.idTipo,
          observaciones: l.observaciones.trim() === '' ? null : l.observaciones.trim(),
          idPlanLinea: l.idPlanLinea,
        })),
        idPlan: propuesta?.idPlan ?? null,
      } satisfies DatosOtManual);
      alCrear(ot);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  function alPulsarCrear(): void {
    if (hayCoberturaParcial && !confirmarParcial) {
      setConfirmarParcial(true);
      return;
    }
    void enviar();
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Nueva OT — manual / extraordinaria</h2>
        <button type="button" className="pequeno" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <p className="pequena suave">
          Origen «Manual / Extraordinaria» — la crea directamente un usuario, nunca el generador. Opcionalmente
          puede basarse en un plan para proponer sus tareas; a partir de aquí sigue exactamente el mismo camino
          que cualquier otra OT.
        </p>

        {centroFijo === null && (
          <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo" style={{ margin: 0, minWidth: 220 }}>
              <label htmlFor="not-cliente">Cliente</label>
              <select id="not-cliente" value={idCliente} onChange={(e) => elegirCliente(e.target.value)}>
                <option value="">Elige un cliente…</option>
                {(clientes.datos?.clientes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razonSocial}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo" style={{ margin: 0, minWidth: 220 }}>
              <label htmlFor="not-centro">Centro</label>
              <select
                id="not-centro"
                value={centro?.id ?? ''}
                disabled={idCliente === ''}
                onChange={(e) => elegirCentro(e.target.value)}
              >
                <option value="">{idCliente === '' ? 'Elige un cliente primero' : 'Elige un centro…'}</option>
                {(centrosDelCliente.datos?.centros ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.descripcion} ({c.codigo})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {centro !== null && (
          <>
            <div className="fila sep" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="campo" style={{ margin: 0, minWidth: 220 }}>
                <label htmlFor="not-sala">Sala</label>
                <select
                  id="not-sala"
                  value={idSala}
                  onChange={(e) => {
                    setIdSala(e.target.value);
                    //  C2: cualquier tarea propuesta o ubicación elegida a
                    //  mano estaba atada a la sala anterior — no sigue
                    //  siendo válida bajo la nueva.
                    limpiarPropuesta();
                  }}
                >
                  <option value="">Elige una sala…</option>
                  {salas.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.descripcion} ({s.codigo})
                    </option>
                  ))}
                </select>
              </div>
              <div className="campo" style={{ margin: 0, maxWidth: 170 }}>
                <label htmlFor="not-fecha">Fecha</label>
                <input id="not-fecha" type="date" value={fechaValor} onChange={(e) => setFechaValor(e.target.value)} />
              </div>
              <div className="campo" style={{ margin: 0, maxWidth: 130 }}>
                <label htmlFor="not-hora">Hora prevista</label>
                <input id="not-hora" type="time" value={horaValor} onChange={(e) => setHoraValor(e.target.value)} />
              </div>
            </div>

            <div className="fila sep" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="campo" style={{ margin: 0, minWidth: 260 }}>
                <label htmlFor="not-plan">Basar en plan</label>
                <select
                  id="not-plan"
                  value={codigoPlan}
                  disabled={codigoPlanFijo !== undefined}
                  onChange={(e) => {
                    setCodigoPlan(e.target.value);
                    //  C2: cambiar de plan (incluido volver a «Sin plan»)
                    //  invalida por igual cualquier propuesta o línea ya
                    //  cargada — nunca se mezclan tareas de dos planes
                    //  distintos, ni las de un plan con las de «Sin plan».
                    limpiarPropuesta();
                  }}
                >
                  <option value="">Sin plan</option>
                  {familias.map((f) => (
                    <option key={f.codigo} value={f.codigo}>
                      {f.descripcion} ({f.codigo})
                    </option>
                  ))}
                </select>
              </div>
              {codigoPlan !== '' && (
                <button
                  type="button"
                  className="pequeno"
                  disabled={idSala === '' || fechaValor === '' || cargandoPropuesta}
                  onClick={() => {
                    void cargarPropuesta();
                  }}
                >
                  {cargandoPropuesta ? 'Cargando…' : propuesta === null ? 'Cargar tareas del plan' : 'Recargar tareas del plan'}
                </button>
              )}
            </div>
            {codigoPlan !== '' && idSala === '' && (
              <p className="pequena tenue sep">Elige una sala para poder cargar las tareas de este plan.</p>
            )}
            {errorPropuesta !== null && <ErrorCaja error={errorPropuesta} />}

            {propuesta !== null && fechaCargada !== null && fechaValor !== fechaCargada && (
              <div className="nota aviso sep">
                <p style={{ margin: 0 }}>La fecha ha cambiado. Las tareas previstas por el plan pueden ser distintas.</p>
                <div className="fila" style={{ gap: 8, marginTop: 8 }}>
                  <button type="button" className="pequeno" onClick={() => void cargarPropuesta()}>
                    Recalcular desde el plan
                  </button>
                  <button type="button" className="pequeno" onClick={() => setFechaCargada(fechaValor)}>
                    Conservar tareas actuales
                  </button>
                </div>
              </div>
            )}

            {opcionesSN.length > 0 && (
              <div className="sep">
                <span className="etiqueta-dato">Tareas según necesidad de este plan (opcionales)</span>
                <div className="lista-checks" style={{ marginTop: 6 }}>
                  {opcionesSN.map((o) => (
                    <label key={o.tarea.idPlanLinea} className="fila" style={{ gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" checked={o.incluida} onChange={() => alternarSN(o.tarea.idPlanLinea)} />
                      <span className="pequena">
                        {o.tarea.descripcionTarea ?? o.tarea.referenciaTarea} — <span className="suave">{o.tarea.codigoUbicacion}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="campo sep">
              <label htmlFor="not-motivo">Descripción / motivo</label>
              <textarea
                id="not-motivo"
                rows={2}
                placeholder="Qué ha pasado y por qué se emite esta OT fuera de plan…"
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
              />
            </div>

            {idSala !== '' && (
              <div className="sep">
                <span className="etiqueta-dato">Líneas de trabajo</span>
                {lineas.map((l, i) => {
                  const tareaDePropuesta = propuesta?.tareas.find((t) => t.idPlanLinea === l.idPlanLinea);
                  return (
                  <div key={l.clave} className="lote-bulk-fila" style={{ marginTop: 8 }}>
                    <div className="lote-bulk-fila-cabecera">
                      <span className="pequena suave">
                        Línea {i + 1}
                        {l.idPlanLinea !== null && <span className="etiqueta info pequena" style={{ marginLeft: 8 }}>Del plan</span>}
                        {tareaDePropuesta?.yaCubierta === true && (
                          <span className="etiqueta aviso pequena" style={{ marginLeft: 8 }}>
                            Ya cubierta por otra OT
                          </span>
                        )}
                      </span>
                      {lineas.length > 1 && (
                        <button type="button" className="pequeno" onClick={() => quitarLinea(l.clave)}>
                          Quitar
                        </button>
                      )}
                    </div>
                    <div className="campo">
                      <span className="etiqueta-dato">Ubicación</span>
                      <SelectorUbicacionEnSala
                        ubicaciones={ubicaciones.datos?.ubicaciones ?? []}
                        idSala={idSala}
                        valor={l.idUbicacion}
                        onSeleccionar={(id) => actualizarLinea(l.clave, { idUbicacion: id })}
                      />
                    </div>
                    <div className="fila" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div className="campo" style={{ margin: 0, minWidth: 200 }}>
                        <label htmlFor={`not-tipo-${l.clave}`}>Tipo de limpieza</label>
                        <select
                          id={`not-tipo-${l.clave}`}
                          value={l.idTipo}
                          onChange={(e) => actualizarLinea(l.clave, { idTipo: e.target.value })}
                        >
                          <option value="">Elige un tipo…</option>
                          {tiposActivos.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.descripcion} ({t.codigo})
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="text"
                        aria-label={`Observación — línea ${String(i + 1)}`}
                        placeholder="Observación de la línea (opcional)"
                        value={l.observaciones}
                        onChange={(e) => actualizarLinea(l.clave, { observaciones: e.target.value })}
                        style={{ flex: 1, minWidth: 160 }}
                      />
                    </div>
                  </div>
                  );
                })}
                <button
                  type="button"
                  className="pequeno"
                  style={{ marginTop: 8 }}
                  onClick={() => setLineas((prev) => [...prev, nuevaLineaVacia()])}
                >
                  + Añadir línea
                </button>
              </div>
            )}
          </>
        )}

        {confirmarParcial && (
          <div className="nota aviso sep">
            <p style={{ margin: 0 }}>
              Cubiertas por esta OT: <strong>{recurrentesCubiertas.length}</strong>
              <br />
              Pendientes del plan: <strong>{recurrentesPropuestas.length - recurrentesCubiertas.length}</strong>
            </p>
            <p className="pequena suave">
              Si posteriormente generas las OTs automáticas, NeatOS podrá crear otra OT para las tareas pendientes
              de esta fecha.
            </p>
            <div className="fila" style={{ gap: 8, marginTop: 8 }}>
              <button type="button" className="pequeno" onClick={incluirTodasYEnviar} disabled={enviando}>
                Incluir todas las tareas previstas
              </button>
              <button
                type="button"
                className="pequeno"
                disabled={enviando}
                onClick={() => {
                  void enviar();
                }}
              >
                Continuar con selección parcial
              </button>
            </div>
          </div>
        )}

        <button className="principal sep" disabled={!puedeEnviar || enviando} onClick={alPulsarCrear}>
          {enviando ? 'Creando…' : 'Crear OT'}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- detalle

export function PaginaOt(): ReactNode {
  const { idOt = '' } = useParams();
  const conectividad = useConectividadAlEntrar();
  const estado = useDatos(() => api.get<OtDetalle>(`/api/ots/${idOt}`), [idOt]);
  //  FASE 9.6-A: consumos de inventario (sólo quien gestiona Limpieza; OPERARIO ni los pide).
  const { puede } = useSesion();
  const consumosOt = useConsumosOt(idOt, puede('consumos_ot:leer'));
  const [validarConsumos, setValidarConsumos] = useState(false);
  //  F10.0: línea No OK para la que se abre la NC (formulario de NC con esa línea ya marcada).
  const [ncParaLinea, setNcParaLinea] = useState<string | null>(null);
  const recargarTodo = (): void => { estado.recargar(); consumosOt.recargar(); };
  //  F10.1: si se llegó desde «Hoy» o el Planner, la miga vuelve ahí, con su centro, fecha y filtro.
  const volver = (useLocation().state as { volver?: { a: string; texto: string } } | null)?.volver ?? null;

  if (conectividad === 'OFFLINE') {
    return <Navigate to={`/offline/${idOt}`} replace state={{ avisoOffline: MENSAJE_OFFLINE_OTS }} />;
  }
  if (estado.cargando && estado.datos === null) {
    return (
      <Shell titulo="Orden de trabajo">
        <Cargando />
      </Shell>
    );
  }
  if (estado.error !== null || estado.datos === null) {
    return (
      <Shell titulo="Orden de trabajo">
        <ErrorCaja error={estado.error} reintentar={estado.recargar} />
      </Shell>
    );
  }

  const ot = estado.datos;

  return (
    <Shell
      migas={[
        volver ?? { texto: 'Órdenes de trabajo', a: '/ots' },
        { texto: ot.codigo },
      ]}
      titulo={
        // FASE 3.3-D4: código + hasta 3 badges puede pasar de 400px de
        // ancho — `.fila` no envuelve por defecto (se usa en muchos
        // sitios como fila de una sola línea), así que aquí sí se fuerza
        // para no desbordar en 360-390px cuando coinciden emisión>1 y
        // estructura cerrada.
        <span className="fila" style={{ flexWrap: 'wrap', rowGap: 4 }}>
          <Avatar nombre={ot.clienteRazonSocial} logoUrl={ot.clienteLogoUrl} pequeno />
          <span className="mono">{ot.codigo}</span>
        </span>
      }
      //  UX-1C: el estado va en su sitio de la cabecera (StatusBadge, junto al título); emisión y
      //  estructura son metadatos, no estado.
      estado={<EstadoOt estado={ot.estado} />}
      meta={
        <>
          <Link to={`/clientes/${ot.idCliente}`}>{ot.clienteRazonSocial}</Link>
          <Link to={`/centros/${ot.idCentro}`}>{ot.centroDescripcion}</Link>
          {ot.emision > 1 && <span className="etiqueta info">emisión {ot.emision}</span>}
        </>
      }
    >
      {/*
        UX-POLISH-01 punto 2: en móvil, Líneas —la tarea real de quien
        está delante de la OT en campo— pasa por delante de los
        metadatos administrativos, que ya conoce por contexto (está en
        ESA sala, de ESE cliente). En escritorio esto no cambia nada:
        `.ot-detalle-secciones` sólo se convierte en columna reordenable
        por debajo de 900px (ver estilos.css) — sin esa media query,
        `order` no tiene ningún efecto y el documento sigue en el orden
        de siempre.
      */}
      <AvisoConsumosPendientes datos={consumosOt.datos} alValidar={() => setValidarConsumos(true)} />
      <ResumenEjecucionOt ot={ot} />
      <div className="ot-detalle-secciones">
        <div className="ot-seccion-datos panel">
          <div className="panel-cuerpo">
            <div className="datos">
              <D e="Cliente" v={ot.clienteRazonSocial} a={`/clientes/${ot.idCliente}`} />
              <D
                e="Centro"
                v={`${ot.centroDescripcion} (${ot.centroCodigo})`}
                a={`/centros/${ot.idCentro}`}
              />
              <D e="Sala" v={`${ot.salaDescripcion} (${ot.salaCodigo})`} />
              <D e="Fecha planificada" v={fechaYHoraOt(ot)} />
              <D e="Fecha límite" v={fecha(ot.fechaLimite)} />
              <div className="dato">
                <div className="etiqueta-dato">Origen</div>
                <div className="valor-dato">
                  <EstadoOrigen origen={ot.origen} />
                </div>
              </div>
              <div className="dato">
                <div className="etiqueta-dato">Plan</div>
                <div className="valor-dato">
                  {ot.planCodigo === null ? (
                    '—'
                  ) : (
                    <span className="fila" style={{ gap: 8 }}>
                      <span>
                        {ot.planCodigo} v{ot.planVersion}
                      </span>
                      {/*  No hay ficha de plan (no existe esa vista global
                          todavía): se enlaza a la pestaña de planificación
                          del centro, no se aparenta un destino que no existe. */}
                      <Link to={`/centros/${ot.idCentro}/planificacion`} className="etiqueta info">
                        Ver planificación
                      </Link>
                    </span>
                  )}
                </div>
              </div>
              <D e="Emisión" v={String(ot.emision)} />
            </div>
            {ot.observaciones !== null && ot.observaciones !== '' && (
              <div className="sep">
                <div className="etiqueta-dato">Observaciones</div>
                <div>{ot.observaciones}</div>
              </div>
            )}
          </div>
        </div>

        {ot.cadena.length > 1 && (
          <div className="ot-seccion-cadena">
            <Cadena ot={ot} />
          </div>
        )}

        <div className="ot-seccion-lineas">
          <LineasOt ot={ot} recargar={recargarTodo} abrirNcDeLinea={setNcParaLinea} />
        </div>

        {consumosOt.datos !== null && (
          <div className="ot-seccion-consumos">
            <ConsumosOtSeccion ot={ot} datos={consumosOt} alCambiar={estado.recargar}
              validarTodos={validarConsumos} alCerrarValidarTodos={() => setValidarConsumos(false)} />
          </div>
        )}

        <div className="ot-seccion-evidencias">
          {/*
            Ámbito OT explícito: sólo la evidencia subida directamente sobre
            la OT (id_ot_linea/id_no_conformidad ambos NULL) — nunca mezclada
            con la de sus líneas o sus NC (encargo 4.4-D, sección 3: "cada
            contexto debe ser explícito"). La de cada línea vive en su propia
            fila/tarjeta, más abajo.
          */}
          <Evidencias ambito="OT" ruta={`/api/ots/${ot.id}/evidencias`} titulo="Evidencias de la OT" />
        </div>

        <div className="ot-seccion-documentos">
          {/*
            Sección propia, sólo lectura: lo que el Plan tenía asociado en
            el momento de emitir esta OT (snapshot de 0044) — nunca se
            recalcula al abrir la OT (encargo 4.4-F, sección 10).
          */}
          <DocumentosAplicables ruta={`/api/ots/${ot.id}/documentos`} titulo="Documentación aplicable a la OT" />
        </div>

        <div className="ot-seccion-nc">
          <SeccionNoConformidades
            ot={ot}
            recargarOt={estado.recargar}
            lineaPreseleccionada={ncParaLinea}
            alCerrarPreseleccion={() => setNcParaLinea(null)}
          />
        </div>

        {/* FASE 7.1: incidencias de esta OT, y «Reportar incidencia» con la OT (o la línea) ya fijada. */}
        <div className="ot-seccion-incidencias">
          <SeccionIncidenciasOt ot={ot} />
        </div>
      </div>
    </Shell>
  );
}

/** «09/10/2026 · 07:00» con la hora local del inicio previsto; sólo la fecha si no se conoce la hora. */
function fechaYHoraOt(ot: OtDetalle): string {
  if (ot.inicioProgramadoEn === undefined || ot.inicioProgramadoEn === null) return fecha(ot.fechaPlan);
  const hora = new Date(ot.inicioProgramadoEn).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${fecha(ot.fechaPlan)} · ${hora}`;
}

/**
 * F10.2 — lo primero al abrir una OT: cuánto está hecho, quién está asignado y, si ya terminó la
 * ejecución, qué falta para cerrarla. Avance = líneas CONFIRMADA/VERIFICADA/NO_OK/NO_APLICA (la misma
 * frontera que «Hoy»); la calidad va aparte: 100 % ejecutada no es 100 % aceptada. Lo que falta lo
 * redacta el servidor (`pendienteParaCerrar`), no se recalcula aquí.
 */
function ResumenEjecucionOt({ ot }: { ot: OtDetalle }): ReactNode {
  const total = ot.lineas.length;
  const hechas = ot.lineas.filter((l) => ['CONFIRMADA', 'VERIFICADA', 'NO_OK', 'NO_APLICA'].includes(l.estado)).length;
  const porId = new Map<string, LineaOt['asignados'][number]>();
  for (const l of ot.lineas) for (const a of l.asignados) porId.set(a.idOperario, a);
  const asignados = [...porId.values()];
  const pendiente = ot.pendienteParaCerrar ?? [];
  return (
    <div className="panel" aria-label="Resumen de ejecución">
      <div className="panel-cuerpo">
        <div className="datos">
          <div className="dato">
            <div className="etiqueta-dato">Avance</div>
            <div className="valor-dato">
              {total === 0 ? 'Sin tareas' : `${String(hechas)}/${String(total)} tareas · ${String(Math.round((hechas * 100) / total))} %`}
            </div>
          </div>
          <div className="dato">
            <div className="etiqueta-dato">Asignados</div>
            <div className="valor-dato">{asignados.length === 0 ? 'Sin asignar' : nombresOperarios(asignados, asignados.length)}</div>
          </div>
        </div>
        {pendiente.length > 0 && (
          <div className="nota aviso sep" role="note">
            <strong>{ot.estado === 'COMPLETADA' ? 'Ejecución terminada. Para verificar y cerrar esta OT falta:' : 'Para cerrar esta OT falta:'}</strong>
            <ul style={{ margin: '4px 0 0' }}>
              {pendiente.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------- ejecución (4.3-A)

type TipoAccionLinea = 'confirmar' | 'verificar' | 'no-ok' | 'no-aplica';

interface AccionActiva {
  readonly idLinea: string;
  readonly tipo: TipoAccionLinea;
}

const RUTA_ACCION: Record<TipoAccionLinea, string> = {
  confirmar: 'confirmar',
  verificar: 'verificar',
  'no-ok': 'no-ok',
  'no-aplica': 'no-aplica',
};

/**
 * Semántica visual de cada acción, reutilizando los tokens de color ya
 * existentes (`--marca`/`--exito`/`--aviso`/`--error`, ver `.etiqueta.*`)
 * — nunca una paleta paralela. Confirmar es la acción principal; Verificar
 * es una validación igualmente positiva; No OK es el resultado negativo;
 * No aplica es una excepción, no un error.
 */
const CLASE_ACCION: Record<TipoAccionLinea, string> = {
  confirmar: 'principal',
  verificar: 'exito',
  'no-ok': 'peligro',
  'no-aplica': 'aviso',
};

/**
 * F10.2: el panel nunca se llama igual que el botón que lo abre, y el envío dice qué se registra.
 * `rehacer` no es un tipo propio: es CONFIRMAR sobre una línea No OK (0077).
 */
const PANEL_ACCION: Record<TipoAccionLinea | 'rehacer', { readonly titulo: string; readonly envio: string }> = {
  confirmar: { titulo: 'Confirmar ejecución', envio: 'Registrar confirmación' },
  verificar: { titulo: 'Verificar resultado', envio: 'Registrar verificación' },
  'no-ok': { titulo: 'Registrar resultado No OK', envio: 'Registrar No OK' },
  'no-aplica': { titulo: 'Marcar como no aplicable', envio: 'Registrar No aplica' },
  rehacer: { titulo: 'Rehacer tarea', envio: 'Registrar corrección' },
};

/** F10.2: objetivo táctil mínimo de las acciones de línea (sin tocar la hoja de estilos global). */
const ESTILO_TACTIL = { minHeight: 44 } as const;

const BOTONES_ACCION: readonly { tipo: TipoAccionLinea; permiso: string; etiqueta: string }[] = [
  { tipo: 'confirmar', permiso: 'ot_lineas:confirmar', etiqueta: 'Confirmar' },
  { tipo: 'verificar', permiso: 'ot_lineas:verificar', etiqueta: 'Verificar' },
  { tipo: 'no-ok', permiso: 'ot_lineas:no_ok', etiqueta: 'No OK' },
  { tipo: 'no-aplica', permiso: 'ot_lineas:no_aplicar', etiqueta: 'No aplica' },
];

function admiteAccion(l: LineaOt, tipo: TipoAccionLinea): boolean {
  switch (tipo) {
    case 'confirmar':
      return l.accionesPermitidas.confirmar;
    case 'verificar':
      return l.accionesPermitidas.verificar;
    case 'no-ok':
      return l.accionesPermitidas.noOk;
    case 'no-aplica':
      return l.accionesPermitidas.noAplica;
  }
}

// ------------------------------------------------- operaciones masivas (4.3-B)

/**
 * "Tiene sentido operativo" mostrar el checkbox: la línea admite AL MENOS
 * una de las dos acciones que el bulk sabe hacer. Es un hint —igual que
 * `accionesPermitidas`—, nunca la última palabra: el servidor revalida
 * cada línea del lote igualmente, y una línea puede dejar de admitir la
 * acción entre que se pinta el checkbox y se envía el lote.
 */
function elegibleBulk(l: LineaOt): boolean {
  //  F10.0: rehacer una línea No OK exige describir la corrección — nunca en lote.
  if (l.estado === 'NO_OK') return false;
  return l.accionesPermitidas.confirmar || l.accionesPermitidas.verificar;
}

type TipoBulk = 'confirmar' | 'verificar';

/** Fila editable de un lote abierto — autocontenida, como la exige la API. */
interface FilaBulk {
  readonly idLinea: string;
  ejecutoresReales: string[];
  fechaEfectiva: string;
  observacion: string;
  /** Generada UNA vez al abrir el lote; estable durante reintentos del MISMO lote. */
  readonly idOperacionCliente: string;
}

interface LoteBulk {
  readonly tipo: TipoBulk;
  readonly idsLinea: readonly string[];
  readonly filas: Readonly<Record<string, FilaBulk>>;
}

const ORDEN_ESTADOS_LINEA = ['PENDIENTE', 'CONFIRMADA', 'VERIFICADA', 'NO_OK', 'NO_APLICA'] as const;

/**
 * Resumen de progreso de las líneas (FASE 4.3-D): sólo cuenta lo que ya
 * viene en `ot.lineas` — ninguna métrica nueva persistida, ningún GET
 * adicional. Sólo se pintan los estados con al menos una línea, en un
 * orden fijo (el del propio ciclo de vida), no el de aparición.
 */
function ResumenLineas({ lineas }: { lineas: readonly LineaOt[] }): ReactNode {
  const conteo = new Map<string, number>();
  for (const l of lineas) conteo.set(l.estado, (conteo.get(l.estado) ?? 0) + 1);
  const conDatos = ORDEN_ESTADOS_LINEA.filter((e) => (conteo.get(e) ?? 0) > 0);
  if (conDatos.length === 0) return null;
  return (
    <div className="resumen-lineas">
      {conDatos.map((e) => (
        <span key={e} className="resumen-lineas-item">
          <EstadoLinea estado={e} />
          <span className="mono">{conteo.get(e)}</span>
        </span>
      ))}
    </div>
  );
}

/** F10.0: abre el formulario de NC con esta línea ya marcada (sin pasarlo fila a fila). */
const ContextoNcDeLinea = createContext<((idLinea: string) => void) | null>(null);

/**
 * F10.2: lo escrito en un panel de acción y no enviado, por línea y acción. Abrir otra acción (en esta
 * u otra línea) ya no lo pierde: al volver a esa acción se recupera. Sólo «Cancelar» (descartar a
 * propósito) o un envío correcto lo borran. Vive lo que vive la ficha: no es autoguardado.
 */
interface BorradorAccion {
  readonly motivo: string;
  readonly observacion: string;
}
const ContextoBorradores = createContext<Map<string, BorradorAccion> | null>(null);

/**
 * Contenedor de las líneas de una OT: tabla en escritorio, tarjetas en
 * móvil/tablet (misma idea que `.tabla-ots-desktop`/`.lista-ots-movil` de
 * FASE 3.3-D4, aquí al corte de 900px porque esta tabla lleva más
 * columnas operativas que la de listado). Sólo UNA acción puede estar
 * abierta a la vez en toda la pantalla — abrir otra cierra la anterior sin
 * preguntar, porque no hay nada que perder: ningún campo se ha enviado
 * todavía.
 */
function LineasOt({
  ot,
  recargar,
  abrirNcDeLinea,
}: {
  ot: OtDetalle;
  recargar: () => void;
  abrirNcDeLinea: (idLinea: string) => void;
}): ReactNode {
  const { puede } = useSesion();
  const [accionActiva, setAccionActiva] = useState<AccionActiva | null>(null);
  const [operarios, setOperarios] = useState<OperarioResumen[] | null>(null);
  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const [loteBulk, setLoteBulk] = useState<LoteBulk | null>(null);
  const [asignando, setAsignando] = useState(false);
  const [resultadoBulk, setResultadoBulk] = useState<{ tipo: TipoBulk; resultado: ResultadoAccionMasiva } | null>(
    null,
  );

  //  El bulk sólo sabe confirmar/verificar (4.2-C) — nunca NO_OK/NO_APLICA.
  const puedeBulkConfirmar = puede('ot_lineas:confirmar');
  const puedeBulkVerificar = puede('ot_lineas:verificar');
  const puedeBulk = puedeBulkConfirmar || puedeBulkVerificar;
  const puedeAsignar = puede('ots:asignar');
  //  Pre-F14.2: `?linea=<id>` (desde el detalle NO_OK del Planner) resalta esa línea y la trae a la vista.
  const [parametros] = useSearchParams();
  const lineaDestacada = parametros.get('linea');
  useEffect(() => {
    if (lineaDestacada === null) return;
    const candidatas = [document.getElementById(`linea-${lineaDestacada}`), document.getElementById(`linea-movil-${lineaDestacada}`)];
    const visible = candidatas.find((el) => el !== null && el.offsetParent !== null) ?? candidatas.find((el) => el !== null) ?? null;
    visible?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [lineaDestacada, ot.lineas.length]);

  function asegurarOperarios(): void {
    //  Operarios "relevantes/visibles para el centro de la OT": el mismo
    //  GET /api/operarios que ya usa el resto de la aplicación, filtrado
    //  por idCentro — el ámbito (ENCARGADO acotado a sus centros) lo
    //  aplica el backend con independencia de qué idCentro se pida, no
    //  un filtro añadido aquí. Se pide una sola vez por visita a esta
    //  pantalla, la primera vez que hace falta.
    if (operarios !== null) return;
    void api
      .get<{ operarios: OperarioResumen[] }>(`/api/operarios?idCentro=${ot.idCentro}&activo=true&dominio=LIMPIEZA`)
      .then((r) => setOperarios(r.operarios))
      .catch(() => setOperarios([]));
  }

  function abrir(idLinea: string, tipo: TipoAccionLinea): void {
    setAccionActiva({ idLinea, tipo });
    if (tipo === 'confirmar') asegurarOperarios();
  }

  function cerrar(): void {
    setAccionActiva(null);
  }

  function alternarSeleccion(idLinea: string): void {
    setSeleccion((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(idLinea)) siguiente.delete(idLinea);
      else siguiente.add(idLinea);
      return siguiente;
    });
  }

  function seleccionarElegibles(): void {
    //  Afecta EXCLUSIVAMENTE a lo cargado ahora mismo en esta OT — nunca
    //  cross-page ni un universo mayor de líneas.
    setSeleccion(new Set(ot.lineas.filter(elegibleBulk).map((l) => l.id)));
  }

  function limpiarSeleccion(): void {
    setSeleccion(new Set());
  }

  function abrirLote(tipo: TipoBulk): void {
    const lineasLote = ot.lineas.filter((l) => seleccion.has(l.id));
    const fechaComun = ahoraParaInput();
    const filas: Record<string, FilaBulk> = {};
    for (const l of lineasLote) {
      filas[l.id] = {
        idLinea: l.id,
        //  BUG 3 (UAT FASE 4.3): cada línea se precarga con SUS PROPIOS
        //  asignados — nunca un ejecutor común para todo el lote. Sin
        //  asignados, queda vacía (no se inventa ejecutor); el usuario
        //  sigue pudiendo editar cada línea antes de enviar.
        ejecutoresReales: tipo === 'confirmar' ? l.asignados.map((a) => a.idOperario) : [],
        fechaEfectiva: fechaComun,
        observacion: '',
        //  Una clave por línea, generada al ABRIR el lote — nunca
        //  compartida entre líneas, y estable si este mismo lote se
        //  reintenta sin cerrarlo.
        idOperacionCliente: crypto.randomUUID(),
      };
    }
    setLoteBulk({ tipo, idsLinea: lineasLote.map((l) => l.id), filas });
    setResultadoBulk(null);
    if (tipo === 'confirmar') asegurarOperarios();
  }

  function actualizarFilaLote(idLinea: string, cambios: Partial<FilaBulk>): void {
    setLoteBulk((prev) => {
      if (prev === null) return null;
      const filaActual = prev.filas[idLinea];
      if (filaActual === undefined) return prev;
      return { ...prev, filas: { ...prev.filas, [idLinea]: { ...filaActual, ...cambios } } };
    });
  }

  function alTerminarLote(tipo: TipoBulk, resultado: ResultadoAccionMasiva): void {
    setResultadoBulk({ tipo, resultado });
    setLoteBulk(null);
    //  Se limpia sólo lo que terminó bien (procesada, con o sin
    //  yaEstaba): lo rechazado se queda seleccionado, para poder
    //  revisarlo o reintentarlo sin volver a marcarlo a mano.
    const idsProcesadas = new Set(resultado.procesadas.map((p) => p.idLinea));
    setSeleccion((prev) => {
      const siguiente = new Set(prev);
      for (const id of idsProcesadas) siguiente.delete(id);
      return siguiente;
    });
    recargar();
  }

  const columnas = puedeBulk ? 7 : 6;
  const borradores = useRef(new Map<string, BorradorAccion>()).current;

  return (
    <ContextoNcDeLinea.Provider value={abrirNcDeLinea}>
    <ContextoBorradores.Provider value={borradores}>
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Líneas</h2>
        <span className="suave pequena">{ot.lineas.length} tarea(s)</span>
      </div>
      <div className="panel-cuerpo sin-relleno">
        {ot.lineas.length === 0 ? (
          <Vacio titulo="Esta orden no tiene líneas" />
        ) : (
          <>
            <ResumenLineas lineas={ot.lineas} />

            {puedeBulk && (
              <BarraSeleccionMasiva
                seleccionadas={seleccion.size}
                puedeConfirmar={puedeBulkConfirmar}
                puedeVerificar={puedeBulkVerificar}
                onSeleccionarElegibles={seleccionarElegibles}
                onLimpiar={limpiarSeleccion}
                onConfirmar={() => abrirLote('confirmar')}
                onVerificar={() => abrirLote('verificar')}
              />
            )}

            {puedeAsignar && (
              <div className="barra-bulk">
                <button type="button" className="pequeno" onClick={() => { asegurarOperarios(); setAsignando(true); }}>
                  Asignar operarios
                </button>
                <span className="pequena suave">La asignación planifica cobertura; no confirma ejecución.</span>
              </div>
            )}

            {asignando && (
              <PanelAsignacionOt
                ot={ot}
                operarios={operarios}
                lineasSeleccionadas={ot.lineas.filter((l) => seleccion.has(l.id))}
                cancelar={() => setAsignando(false)}
                terminar={() => { setAsignando(false); recargar(); }}
              />
            )}

            {resultadoBulk !== null && (
              <ResultadoBulkPanel
                tipo={resultadoBulk.tipo}
                resultado={resultadoBulk.resultado}
                lineas={ot.lineas}
                cerrar={() => setResultadoBulk(null)}
              />
            )}

            {loteBulk !== null && (
              <PanelBulk
                lote={loteBulk}
                lineas={ot.lineas.filter((l) => loteBulk.idsLinea.includes(l.id))}
                operarios={operarios}
                cancelar={() => setLoteBulk(null)}
                actualizarFila={actualizarFilaLote}
                alTerminar={(resultado) => alTerminarLote(loteBulk.tipo, resultado)}
              />
            )}

            <div className="tabla-desliza tabla-lineas-desktop">
              <table>
                <thead>
                  <tr>
                    {puedeBulk && <th className="col-check" aria-hidden="true" />}
                    <th className="num">#</th>
                    <th>Tarea</th>
                    <th>Estado</th>
                    <th>Ejecución</th>
                    <th>Atención</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ot.lineas.map((l) => (
                    <FilaLinea
                      key={l.id}
                      idOt={ot.id}
                      linea={l}
                      destacada={l.id === lineaDestacada}
                      accionActiva={accionActiva}
                      operarios={operarios}
                      abrir={(tipo) => abrir(l.id, tipo)}
                      cerrar={cerrar}
                      recargarOt={recargar}
                      columnas={columnas}
                      puedeBulk={puedeBulk}
                      seleccionada={seleccion.has(l.id)}
                      onSeleccionar={() => alternarSeleccion(l.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="lista-lineas-movil">
              {ot.lineas.map((l) => (
                <TarjetaLinea
                  key={l.id}
                  idOt={ot.id}
                  linea={l}
                  destacada={l.id === lineaDestacada}
                  accionActiva={accionActiva}
                  operarios={operarios}
                  abrir={(tipo) => abrir(l.id, tipo)}
                  cerrar={cerrar}
                  recargarOt={recargar}
                  puedeBulk={puedeBulk}
                  seleccionada={seleccion.has(l.id)}
                  onSeleccionar={() => alternarSeleccion(l.id)}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
    </ContextoBorradores.Provider>
    </ContextoNcDeLinea.Provider>
  );
}

/**
 * Barra de selección masiva: nunca fija/pegajosa (no debe tapar
 * contenido ni navegación en móvil) — fluye con el panel, justo encima
 * de la tabla/tarjetas. "Seleccionar elegibles" y el contador son
 * puramente informativos de UI: la elegibilidad real la vuelve a decidir
 * el servidor al procesar el lote.
 */
function PanelAsignacionOt({
  ot, operarios, lineasSeleccionadas, cancelar, terminar,
}: {
  ot: OtDetalle; operarios: OperarioResumen[] | null; lineasSeleccionadas: readonly LineaOt[];
  cancelar: () => void; terminar: () => void;
}): ReactNode {
  const [alcance, setAlcance] = useState<'TODA_OT' | 'LINEAS'>('TODA_OT');
  const [operacion, setOperacion] = useState<'ANADIR' | 'SUSTITUIR' | 'RETIRAR'>('ANADIR');
  const [idsOperarios, setIdsOperarios] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const idsLineas = lineasSeleccionadas.map((l) => l.id);
  function alternar(id: string): void { setIdsOperarios((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  async function aplicar(): Promise<void> {
    if (idsOperarios.length === 0 || (alcance === 'LINEAS' && idsLineas.length === 0)) {
      setError(new Error(alcance === 'LINEAS' ? 'Selecciona al menos una línea y un operario.' : 'Selecciona al menos un operario.'));
      return;
    }
    setEnviando(true); setError(null);
    try {
      await api.post(`/api/ots/${ot.id}/asignaciones`, { operacion, alcance, idsOperarios, idsLineas });
      terminar();
    } catch (e) { setError(e); } finally { setEnviando(false); }
  }
  return <div className="panel accion-linea">
    <div className="panel-cabecera"><h3>Asignar operarios</h3><button type="button" className="pequeno" onClick={cancelar}>Cancelar</button></div>
    <div className="panel-cuerpo">
      {error !== null && <ErrorCaja error={error} />}
      <p className="pequena suave">Cobertura actual: {ot.lineas.map((l) => `${l.ubicacionCodigo}: ${nombresOperarios(l.asignados, l.asignados.length) || 'sin asignar'}`).join(' · ')}</p>
      <div className="fila" style={{ gap: 16, flexWrap: 'wrap' }}>
        <label><input type="radio" checked={alcance === 'TODA_OT'} onChange={() => setAlcance('TODA_OT')} /> Toda la OT</label>
        <label><input type="radio" checked={alcance === 'LINEAS'} onChange={() => setAlcance('LINEAS')} /> Líneas seleccionadas ({idsLineas.length})</label>
      </div>
      <div className="fila sep" style={{ gap: 16, flexWrap: 'wrap' }}>
        <label><input type="radio" checked={operacion === 'ANADIR'} onChange={() => setOperacion('ANADIR')} /> Añadir</label>
        <label><input type="radio" checked={operacion === 'SUSTITUIR'} onChange={() => setOperacion('SUSTITUIR')} /> Sustituir</label>
        <label><input type="radio" checked={operacion === 'RETIRAR'} onChange={() => setOperacion('RETIRAR')} /> Retirar</label>
      </div>
      <p className="pequena suave">{idsLineas.length} líneas seleccionadas · {idsOperarios.length} operarios seleccionados</p>
      <div className="lista-checks lista-checks-horizontal">
        {operarios === null ? <Cargando texto="Cargando operarios…" /> : operarios.map((op) => <label key={op.id} className="fila" style={{ gap: 4 }}><input type="checkbox" checked={idsOperarios.includes(op.id)} onChange={() => alternar(op.id)} /> <span>{op.nombre} {op.apellidos} ({op.codigo})</span></label>)}
      </div>
      <button type="button" className={operacion === 'RETIRAR' ? 'peligro' : 'principal'} disabled={enviando} onClick={() => void aplicar()}>
        {enviando ? 'Aplicando…' : `${operacion === 'RETIRAR' ? 'Retirar' : operacion === 'SUSTITUIR' ? 'Sustituir' : 'Añadir'} asignación`}
      </button>
    </div>
  </div>;
}

function BarraSeleccionMasiva({
  seleccionadas,
  puedeConfirmar,
  puedeVerificar,
  onSeleccionarElegibles,
  onLimpiar,
  onConfirmar,
  onVerificar,
}: {
  seleccionadas: number;
  puedeConfirmar: boolean;
  puedeVerificar: boolean;
  onSeleccionarElegibles: () => void;
  onLimpiar: () => void;
  onConfirmar: () => void;
  onVerificar: () => void;
}): ReactNode {
  return (
    <div className="barra-bulk">
      <div className="fila barra-bulk-fila">
        <button type="button" className="pequeno" onClick={onSeleccionarElegibles}>
          Seleccionar elegibles
        </button>
        <button type="button" className="pequeno" onClick={onLimpiar} disabled={seleccionadas === 0}>
          Limpiar selección
        </button>
        <span className="pequena suave barra-bulk-contador">
          {seleccionadas} seleccionada{seleccionadas === 1 ? '' : 's'}
        </span>
        <span className="barra-bulk-relleno" />
        {puedeConfirmar && (
          <button type="button" className="principal" disabled={seleccionadas === 0} onClick={onConfirmar}>
            Confirmar seleccionadas ({seleccionadas})
          </button>
        )}
        {puedeVerificar && (
          <button type="button" className="exito" disabled={seleccionadas === 0} onClick={onVerificar}>
            Verificar seleccionadas ({seleccionadas})
          </button>
        )}
      </div>
    </div>
  );
}

/** Botones de acción de una línea: permiso Y máquina, las dos — nunca una sola. */
function BotonesAccionLinea({
  linea,
  accionAbierta,
  abrir,
}: {
  linea: LineaOt;
  accionAbierta: boolean;
  abrir: (tipo: TipoAccionLinea) => void;
}): ReactNode {
  const { puede } = useSesion();
  const abrirNc = useContext(ContextoNcDeLinea);
  //  Sólo lo que admite la máquina (accionesPermitidas del servidor) Y el permiso — nunca reglas propias.
  const disponibles = BOTONES_ACCION.filter((b) => puede(b.permiso) && admiteAccion(linea, b.tipo));
  //  F10.0 (0077): una línea No OK se resuelve rehaciéndola, y para eso necesita antes su NC.
  const faltaNc = linea.estado === 'NO_OK' && linea.ncAsociadas === 0;
  const ofrecerNc = faltaNc && abrirNc !== null && puede('nc:crear');
  if (disponibles.length === 0 && !ofrecerNc) {
    return <span className="tenue">{faltaNc ? 'Pendiente de NC' : '—'}</span>;
  }
  //  F10.2: UNA acción principal (el siguiente paso normal), el resto en segundo plano.
  const principal = ofrecerNc ? null : (disponibles.find((b) => b.tipo === 'confirmar' || b.tipo === 'verificar') ?? null);
  const secundarias = disponibles.filter((b) => b !== principal);
  return (
    <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
      {ofrecerNc && (
        <button type="button" className="pequeno principal" style={ESTILO_TACTIL} disabled={accionAbierta} onClick={() => abrirNc(linea.id)}>
          Abrir NC
        </button>
      )}
      {principal !== null && (
        <button type="button" className="pequeno principal" style={ESTILO_TACTIL} disabled={accionAbierta} onClick={() => abrir(principal.tipo)}>
          {principal.tipo === 'confirmar' && linea.estado === 'NO_OK' ? 'Rehacer' : principal.etiqueta}
        </button>
      )}
      {secundarias.map((b) => (
        <button
          key={b.tipo}
          type="button"
          className={`pequeno${b.tipo === 'no-ok' ? ' peligro' : ''}`}
          style={ESTILO_TACTIL}
          disabled={accionAbierta}
          onClick={() => abrir(b.tipo)}
        >
          {b.etiqueta}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------ presentación de línea (F10.2)

const NOMBRE_ESTADO_NC: Readonly<Record<string, string>> = {
  ABIERTA: 'abierta', EN_TRATAMIENTO: 'en tratamiento', CERRADA: 'cerrada', ANULADA: 'anulada',
};

/** Qué tarea es y dónde. El tipo sólo aparece si dice algo distinto de la tarea. */
function TareaDeLinea({ linea }: { linea: LineaOt }): ReactNode {
  const tarea = linea.descripcionTarea ?? linea.tipoDescripcion;
  return (
    <>
      <div><strong>{tarea}</strong></div>
      <div className="pequena suave">
        <span className="cod">{linea.ubicacionCodigo}</span> · {linea.ubicacionRuta}
      </div>
      {(linea.descripcionTarea !== null || linea.referenciaTarea !== null) && (
        <div className="pequena tenue">
          {linea.referenciaTarea !== null && <span className="cod">{linea.referenciaTarea}</span>}
          {linea.referenciaTarea !== null && linea.descripcionTarea !== null && ' · '}
          {linea.descripcionTarea !== null && linea.tipoDescripcion}
        </div>
      )}
      {linea.reemiteA !== null && <div className="pequena tenue">reemite de {linea.reemiteA}</div>}
      {linea.ocurrencias.length > 1 && (
        <div className="pequena tenue">{linea.ocurrencias.map((f) => fecha(f)).join(', ')}</div>
      )}
    </>
  );
}

function mismosOperarios(a: LineaOt['asignados'], b: LineaOt['asignados']): boolean {
  return a.length === b.length && a.every((x) => b.some((y) => y.idOperario === x.idOperario));
}

/**
 * Quién y qué se hizo. «Ejecutó» sólo si aporta (hay ejecutores y no son exactamente los asignados);
 * confirmación y verificación como hechos separados (BUG 1, FASE 4.3); No aplica con su motivo.
 */
function EjecucionDeLinea({ linea }: { linea: LineaOt }): ReactNode {
  const ejecutoAporta = linea.ejecutoresReales.length > 0 && !mismosOperarios(linea.asignados, linea.ejecutoresReales);
  const ev = linea.eventoRelevante;
  return (
    <>
      <div className="pequena">
        <span className="suave">Asignados:</span>{' '}
        {linea.asignados.length === 0 ? <span className="tenue">sin asignar</span> : nombresOperarios(linea.asignados, linea.asignados.length)}
      </div>
      {ejecutoAporta && (
        <div className="pequena">
          <span className="suave">Ejecutó:</span> {nombresOperarios(linea.ejecutoresReales, linea.ejecutoresReales.length)}
        </div>
      )}
      <HistorialEjecucionLinea linea={linea} />
      {linea.estado === 'NO_APLICA' && ev !== null && ev.motivo !== null && ev.motivo !== '' && (
        <div className="pequena">
          <span className="suave">No aplica:</span> {ev.motivo}
        </div>
      )}
      {ev !== null && ev.observacion !== null && ev.observacion !== '' && (
        <div className="pequena tenue">Nota: {ev.observacion}</div>
      )}
    </>
  );
}

/**
 * Atención de calidad de la línea, compacta: motivo del No OK, su NC (código y estado, enlazada) y el
 * siguiente paso. Fuera de No OK, nada: el resto de estados no piden atención por sí mismos.
 */
function AtencionDeLinea({ linea }: { linea: LineaOt }): ReactNode {
  const ubicacion = useLocation();
  if (linea.estado !== 'NO_OK') return <span className="tenue">—</span>;
  const motivo = linea.eventoRelevante?.motivo ?? null;
  const ncs = linea.ncs ?? [];
  //  Volver de la NC a ESTA OT conservando, a su vez, de dónde se llegó a la OT (Hoy, Planner…).
  const volverAOt = { a: `${ubicacion.pathname}${ubicacion.search}`, texto: 'Volver a la OT', state: ubicacion.state };
  return (
    <div className="pequena" role="group" aria-label="Atención de calidad">
      {/*  El estado «No OK» ya lo dice la columna Estado: aquí, sólo el porqué. */}
      {motivo !== null && motivo !== '' && <div><span className="suave">Motivo:</span> {motivo}</div>}
      {ncs.length === 0 ? (
        <div>Sin no conformidad. <strong>Siguiente paso:</strong> abrir su NC.</div>
      ) : (
        <>
          <div>
            {ncs.map((nc, i) => (
              <span key={nc.id}>
                {i > 0 && ', '}
                <Link to={`/calidad/no-conformidades/${nc.id}`} state={{ volver: volverAOt }}>{nc.codigo}</Link>{' '}
                <span className="suave">({NOMBRE_ESTADO_NC[nc.estado] ?? nc.estado.toLowerCase()})</span>
              </span>
            ))}
          </div>
          <div><strong>Siguiente paso:</strong> rehacer la tarea y describir la corrección.</div>
        </>
      )}
    </div>
  );
}

/** Evidencias, documentos e incidencia: útiles pero no el siguiente paso — juntos, bajo «Más». */
function MasDeLinea({
  idLinea,
  abierto,
  alternar,
  verEvidencias,
  verDocumentos,
}: {
  idLinea: string;
  abierto: boolean;
  alternar: () => void;
  verEvidencias: () => void;
  verDocumentos: () => void;
}): ReactNode {
  const { puede } = useSesion();
  if (!puede('evidencias:leer') && !puede('documentos:leer') && !puede('incidencias:crear')) return null;
  return (
    <>
      <button type="button" className="pequeno" style={ESTILO_TACTIL} aria-expanded={abierto} onClick={alternar}>
        {abierto ? 'Menos' : 'Más'}
      </button>
      {abierto && (
        <span className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
          {puede('evidencias:leer') && (
            <button type="button" className="pequeno" style={ESTILO_TACTIL} onClick={verEvidencias}>Evidencias</button>
          )}
          {puede('documentos:leer') && (
            <button type="button" className="pequeno" style={ESTILO_TACTIL} onClick={verDocumentos}>Documentos</button>
          )}
          <BotonReportarIncidenciaLinea idLinea={idLinea} />
        </span>
      )}
    </>
  );
}

/**
 * Confirmación y verificación como hechos separados (BUG 1, FASE 4.3):
 * CONFIRMADO POR ≠ VERIFICADO POR, y una verificación posterior nunca
 * sustituye ni oculta quién confirmó. Ambos se muestran si existen — nunca
 * uno en lugar del otro.
 */
function HistorialEjecucionLinea({ linea }: { linea: LineaOt }): ReactNode {
  if (linea.confirmacion === null && linea.verificacion === null) return null;
  return (
    <>
      {linea.confirmacion !== null && (
        <div className="pequena tenue">
          Confirmado por {linea.confirmacion.actor.nombre} · {fechaHora(linea.confirmacion.fechaEfectiva)}
        </div>
      )}
      {linea.verificacion !== null && (
        <div className="pequena tenue">
          Verificado por {linea.verificacion.actor.nombre}{linea.verificacion.origen === 'CLIENTE' ? ' (cliente, desde el portal)' : ''} · {fechaHora(linea.verificacion.fechaEfectiva)}
        </div>
      )}
    </>
  );
}

/**
 * Resultado de un lote — se mantiene visible tras la recarga de la OT
 * (nunca se autodescarta) hasta que el usuario lo cierra o abre otro
 * lote. Nunca esconde un rechazo parcial detrás de un "completado"
 * genérico: procesadas nuevas, ya-estaba y rechazadas se cuentan por
 * separado, y cada rechazo lleva su código y su mensaje tal cual los dio
 * el servidor.
 */
function ResultadoBulkPanel({
  tipo,
  resultado,
  lineas,
  cerrar,
}: {
  tipo: TipoBulk;
  resultado: ResultadoAccionMasiva;
  lineas: readonly LineaOt[];
  cerrar: () => void;
}): ReactNode {
  const nuevas = resultado.procesadas.filter((p) => !p.yaEstaba).length;
  const yaEstaban = resultado.procesadas.filter((p) => p.yaEstaba).length;
  const porLinea = new Map(lineas.map((l) => [l.id, l]));

  return (
    <div className="panel resultado-bulk">
      <div className="panel-cabecera">
        <h3>Resultado — {tipo === 'confirmar' ? 'confirmar' : 'verificar'} en lote</h3>
        <button type="button" className="pequeno" onClick={cerrar}>
          Cerrar
        </button>
      </div>
      <div className="panel-cuerpo">
        <div className="cifras">
          <Cifra n={nuevas} t="procesadas" />
          <Cifra n={yaEstaban} t="ya estaban realizadas" />
          <Cifra n={resultado.rechazadas.length} t="rechazadas" />
        </div>
        {resultado.rechazadas.length > 0 && (
          <div className="sep">
            <p className="pequena suave">Líneas rechazadas — el servidor revalidó cada una por separado:</p>
            <ul className="lista-rechazadas">
              {resultado.rechazadas.map((r) => (
                <li key={r.idLinea}>
                  <div className="fila" style={{ gap: 8, alignItems: 'center' }}>
                    <span className="cod pequena">{porLinea.get(r.idLinea)?.ubicacionCodigo ?? r.idLinea}</span>
                    <span className="etiqueta error pequena">{r.codigo}</span>
                  </div>
                  <div className="pequena suave">{r.mensaje}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Formulario de un lote abierto: valores comunes ARRIBA (comodidad
 * explícita, nunca aplicados en silencio — sólo al pulsar "Aplicar a
 * todas") y una fila editable POR LÍNEA debajo, siempre autocontenida:
 * lo que de verdad se envía es lo que hay en cada fila en el momento del
 * envío, nunca el valor común directamente. `asignado ≠ ejecutor real`
 * se mantiene igual que en la acción unitaria: ningún checkbox empieza
 * marcado por su cuenta.
 */
function PanelBulk({
  lote,
  lineas,
  operarios,
  cancelar,
  actualizarFila,
  alTerminar,
}: {
  lote: LoteBulk;
  lineas: readonly LineaOt[];
  operarios: OperarioResumen[] | null;
  cancelar: () => void;
  actualizarFila: (idLinea: string, cambios: Partial<FilaBulk>) => void;
  alTerminar: (resultado: ResultadoAccionMasiva) => void;
}): ReactNode {
  const idBase = useId();
  const esConfirmar = lote.tipo === 'confirmar';
  const [fechaComun, setFechaComun] = useState(ahoraParaInput);
  const [observacionComun, setObservacionComun] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  //  BUG 3 (UAT FASE 4.3): ya NO hay "ejecutores comunes" que aplicar a
  //  todas las líneas — cada línea trae SUS PROPIOS asignados como
  //  propuesta desde `abrirLote`. Aplicar comunes se limita a fecha y
  //  observación, que sí tiene sentido compartir entre líneas del mismo
  //  lote; los ejecutores son, y siguen siendo, por línea.
  function aplicarComunesATodas(): void {
    for (const idLinea of lote.idsLinea) {
      actualizarFila(idLinea, { fechaEfectiva: fechaComun, observacion: observacionComun });
    }
  }

  function alternarEjecutorFila(idLinea: string, id: string): void {
    const actuales = lote.filas[idLinea]?.ejecutoresReales ?? [];
    actualizarFila(idLinea, {
      ejecutoresReales: actuales.includes(id) ? actuales.filter((x) => x !== id) : [...actuales, id],
    });
  }

  async function enviar(): Promise<void> {
    if (esConfirmar) {
      const sinEjecutor = lote.idsLinea.filter((id) => (lote.filas[id]?.ejecutoresReales.length ?? 0) === 0);
      if (sinEjecutor.length > 0) {
        setError(
          new globalThis.Error(
            `${String(sinEjecutor.length)} de las ${String(lote.idsLinea.length)} líneas no tienen ningún ` +
              'ejecutor real marcado. Cada línea es editable por separado, justo debajo.',
          ),
        );
        return;
      }
    }
    setEnviando(true);
    setError(null);
    try {
      const lineasPayload = lote.idsLinea.map((idLinea) => {
        const f = lote.filas[idLinea]!;
        const comun = {
          idLinea,
          fechaEfectiva: new Date(f.fechaEfectiva).toISOString(),
          observacion: f.observacion.trim() === '' ? null : f.observacion.trim(),
          idOperacionCliente: f.idOperacionCliente,
        };
        return esConfirmar ? { ...comun, ejecutoresReales: f.ejecutoresReales } : comun;
      });
      const ruta = esConfirmar ? '/api/ot-lineas/confirmar-masivo' : '/api/ot-lineas/verificar-masivo';
      const resultado = await api.post<ResultadoAccionMasiva>(ruta, { lineas: lineasPayload });
      alTerminar(resultado);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea lote-bulk">
      <div className="panel-cabecera">
        <h3>
          {esConfirmar ? 'Confirmar' : 'Verificar'} {lote.idsLinea.length} línea(s)
        </h3>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}

        <div className="lote-bulk-comunes">
          <p className="pequena suave">
            Fecha y observación comunes: rellena y pulsa «Aplicar a todas» para copiarlas a cada línea de abajo —
            cada una sigue siendo editable por separado antes de enviar. Los ejecutores reales son SIEMPRE por
            línea: cada una aparece más abajo preseleccionada con SUS propios asignados, nunca con un ejecutor
            común para todo el lote.
          </p>
          <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-fecha-comun`}>Fecha efectiva común</label>
              <input
                id={`${idBase}-fecha-comun`}
                type="datetime-local"
                value={fechaComun}
                max={ahoraParaInput()}
                onChange={(e) => setFechaComun(e.target.value)}
              />
            </div>
            <div className="campo" style={{ margin: 0, flex: 1, minWidth: 200 }}>
              <label htmlFor={`${idBase}-obs-comun`}>Observación común (opcional)</label>
              <input
                id={`${idBase}-obs-comun`}
                type="text"
                value={observacionComun}
                onChange={(e) => setObservacionComun(e.target.value)}
              />
            </div>
            <button type="button" className="pequeno" onClick={aplicarComunesATodas}>
              Aplicar a todas
            </button>
          </div>
        </div>

        <div className="lote-bulk-filas">
          {lineas.map((l) => {
            const f = lote.filas[l.id];
            if (f === undefined) return null;
            return (
              <div key={l.id} className="lote-bulk-fila">
                <div className="lote-bulk-fila-cabecera">
                  <span className="cod">{l.ubicacionCodigo}</span>
                  <span className="pequena suave">{l.descripcionTarea ?? l.tipoDescripcion}</span>
                </div>
                {esConfirmar && l.asignados.length === 0 && (
                  <p className="nota aviso pequena">Sin asignados — elige el ejecutor manualmente.</p>
                )}
                {esConfirmar && (
                  <div className="lista-checks lista-checks-horizontal">
                    {operarios === null ? (
                      <Cargando texto="Cargando operarios…" />
                    ) : (
                      [...operarios]
                        .sort((a, b) => {
                          const aAsig = l.asignados.some((x) => x.idOperario === a.id) ? 0 : 1;
                          const bAsig = l.asignados.some((x) => x.idOperario === b.id) ? 0 : 1;
                          return aAsig - bAsig || a.codigo.localeCompare(b.codigo);
                        })
                        .map((op) => {
                          const esAsignado = l.asignados.some((a) => a.idOperario === op.id);
                          return (
                            <label key={op.id} className="fila" style={{ gap: 4 }}>
                              <input
                                type="checkbox"
                                checked={f.ejecutoresReales.includes(op.id)}
                                onChange={() => alternarEjecutorFila(l.id, op.id)}
                              />
                              <span className="pequena">
                                {op.nombre} {op.apellidos} ({op.codigo})
                              </span>
                              {esAsignado && <span className="etiqueta neutra pequena">asignado</span>}
                            </label>
                          );
                        })
                    )}
                  </div>
                )}
                <div className="fila" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <input
                    type="datetime-local"
                    aria-label={`Fecha efectiva — línea ${l.ubicacionCodigo}`}
                    value={f.fechaEfectiva}
                    max={ahoraParaInput()}
                    onChange={(e) => actualizarFila(l.id, { fechaEfectiva: e.target.value })}
                  />
                  <input
                    type="text"
                    aria-label={`Observación — línea ${l.ubicacionCodigo}`}
                    placeholder="Observación (opcional)"
                    value={f.observacion}
                    onChange={(e) => actualizarFila(l.id, { observacion: e.target.value })}
                    style={{ flex: 1, minWidth: 160 }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className={esConfirmar ? 'principal' : 'exito'}
          disabled={enviando}
          onClick={() => void enviar()}
        >
          {enviando
            ? 'Enviando…'
            : `${esConfirmar ? 'Confirmar' : 'Verificar'} ${String(lote.idsLinea.length)} línea(s)`}
        </button>
      </div>
    </div>
  );
}

function FilaLinea({
  idOt,
  linea,
  accionActiva,
  operarios,
  abrir,
  cerrar,
  recargarOt,
  columnas,
  puedeBulk,
  seleccionada,
  onSeleccionar,
  destacada,
}: {
  idOt: string;
  linea: LineaOt;
  /** Pre-F14.2: la línea pedida en `?linea=` (desde el detalle NO_OK del Planner). */
  destacada?: boolean;
  accionActiva: AccionActiva | null;
  operarios: OperarioResumen[] | null;
  abrir: (tipo: TipoAccionLinea) => void;
  cerrar: () => void;
  recargarOt: () => void;
  columnas: number;
  puedeBulk: boolean;
  seleccionada: boolean;
  onSeleccionar: () => void;
}): ReactNode {
  const abierta = accionActiva !== null && accionActiva.idLinea === linea.id;
  const [evidenciasAbiertas, setEvidenciasAbiertas] = useState(false);
  const [documentosAbiertos, setDocumentosAbiertos] = useState(false);
  const [mas, setMas] = useState(false);
  return (
    <>
      <tr id={`linea-${linea.id}`} className={destacada === true ? 'linea-destacada' : undefined} aria-current={destacada === true ? 'true' : undefined}>
        {puedeBulk && (
          <td className="col-check">
            {elegibleBulk(linea) && (
              <input
                type="checkbox"
                aria-label={`Seleccionar línea ${String(linea.orden)} (${linea.ubicacionCodigo})`}
                checked={seleccionada}
                onChange={onSeleccionar}
              />
            )}
          </td>
        )}
        <td className="num">{linea.orden}</td>
        <td>
          <TareaDeLinea linea={linea} />
        </td>
        <td>
          <EstadoLinea estado={linea.estado} />
        </td>
        <td>
          <EjecucionDeLinea linea={linea} />
        </td>
        <td>
          <AtencionDeLinea linea={linea} />
        </td>
        <td>
          <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
            <BotonesAccionLinea linea={linea} accionAbierta={abierta} abrir={abrir} />
            <MasDeLinea
              idLinea={linea.id}
              abierto={mas}
              alternar={() => setMas((v) => !v)}
              verEvidencias={() => setEvidenciasAbiertas((v) => !v)}
              verDocumentos={() => setDocumentosAbiertos((v) => !v)}
            />
          </div>
        </td>
      </tr>
      {abierta && accionActiva !== null && (
        <tr>
          <td colSpan={columnas} className="celda-accion-linea">
            <FormularioAccionLinea
              linea={linea}
              tipo={accionActiva.tipo}
              operarios={operarios}
              cerrar={cerrar}
              recargarOt={recargarOt}
            />
          </td>
        </tr>
      )}
      {evidenciasAbiertas && (
        <tr>
          <td colSpan={columnas} className="celda-accion-linea">
            {/*
              Ámbito LINEA explícito: sólo la evidencia de ESTA línea
              (id_ot_linea = linea.id) — nunca la de la OT completa
              (encargo 4.4-D, sección 3B: "no mezclar automáticamente con
              las de la OT").
            */}
            <Evidencias ambito="LINEA" ruta={`/api/ots/${idOt}/lineas/${linea.id}/evidencias`} titulo="Evidencias de la línea" />
          </td>
        </tr>
      )}
      {documentosAbiertos && (
        <tr>
          <td colSpan={columnas} className="celda-accion-linea">
            <DocumentosAplicables
              ruta={`/api/ots/${idOt}/lineas/${linea.id}/documentos`}
              titulo="Documentación aplicable a la línea"
            />
          </td>
        </tr>
      )}
    </>
  );
}

function TarjetaLinea({
  idOt,
  linea,
  accionActiva,
  operarios,
  abrir,
  cerrar,
  recargarOt,
  puedeBulk,
  seleccionada,
  onSeleccionar,
  destacada,
}: {
  idOt: string;
  linea: LineaOt;
  /** Pre-F14.2: la línea pedida en `?linea=` (desde el detalle NO_OK del Planner). */
  destacada?: boolean;
  accionActiva: AccionActiva | null;
  operarios: OperarioResumen[] | null;
  abrir: (tipo: TipoAccionLinea) => void;
  cerrar: () => void;
  recargarOt: () => void;
  puedeBulk: boolean;
  seleccionada: boolean;
  onSeleccionar: () => void;
}): ReactNode {
  const abierta = accionActiva !== null && accionActiva.idLinea === linea.id;
  const [evidenciasAbiertas, setEvidenciasAbiertas] = useState(false);
  const [documentosAbiertos, setDocumentosAbiertos] = useState(false);
  const [mas, setMas] = useState(false);
  return (
    <li id={`linea-movil-${linea.id}`} className={`tarjeta-linea${destacada === true ? ' linea-destacada' : ''}`} aria-current={destacada === true ? 'true' : undefined}>
      <div className="tarjeta-linea-cabecera">
        <div className="fila" style={{ gap: 8, alignItems: 'flex-start' }}>
          {puedeBulk && elegibleBulk(linea) && (
            <input
              type="checkbox"
              aria-label={`Seleccionar línea ${String(linea.orden)} (${linea.ubicacionCodigo})`}
              checked={seleccionada}
              onChange={onSeleccionar}
              className="check-tarjeta-linea"
            />
          )}
          <div>
            <TareaDeLinea linea={linea} />
          </div>
        </div>
        <EstadoLinea estado={linea.estado} />
      </div>
      <div className="tarjeta-linea-datos">
        <EjecucionDeLinea linea={linea} />
        {linea.estado === 'NO_OK' && <AtencionDeLinea linea={linea} />}
      </div>
      <div className="tarjeta-linea-acciones">
        <BotonesAccionLinea linea={linea} accionAbierta={abierta} abrir={abrir} />
        <MasDeLinea
          idLinea={linea.id}
          abierto={mas}
          alternar={() => setMas((v) => !v)}
          verEvidencias={() => setEvidenciasAbiertas((v) => !v)}
          verDocumentos={() => setDocumentosAbiertos((v) => !v)}
        />
      </div>
      {abierta && accionActiva !== null && (
        <div className="tarjeta-linea-formulario">
          <FormularioAccionLinea
            linea={linea}
            tipo={accionActiva.tipo}
            operarios={operarios}
            cerrar={cerrar}
            recargarOt={recargarOt}
          />
        </div>
      )}
      {evidenciasAbiertas && (
        <div className="tarjeta-linea-formulario">
          <Evidencias ambito="LINEA" ruta={`/api/ots/${idOt}/lineas/${linea.id}/evidencias`} titulo="Evidencias de la línea" />
        </div>
      )}
      {documentosAbiertos && (
        <div className="tarjeta-linea-formulario">
          <DocumentosAplicables
            ruta={`/api/ots/${idOt}/lineas/${linea.id}/documentos`}
            titulo="Documentación aplicable a la línea"
          />
        </div>
      )}
    </li>
  );
}

/**
 * El único formulario de ejecución, parametrizado por `tipo`. `POST →
 * respuesta correcta → recargarOt()` — nunca se pinta nada a partir de la
 * respuesta del POST, la siguiente verdad es el GET. Un error dentro de
 * este formulario deja el formulario abierto (para poder corregir y
 * reintentar) y TAMBIÉN recarga la OT de fondo, por si el rechazo se debe
 * a que la línea cambió entre el render y el envío.
 */
function FormularioAccionLinea({
  linea,
  tipo,
  operarios,
  cerrar,
  recargarOt,
}: {
  linea: LineaOt;
  tipo: TipoAccionLinea;
  operarios: OperarioResumen[] | null;
  cerrar: () => void;
  recargarOt: () => void;
}): ReactNode {
  //  Generada UNA vez al abrir esta acción — inicializador perezoso de
  //  useState, no se recalcula en cada render. Un reintento tras un
  //  fallo (misma apertura de formulario) reutiliza esta MISMA clave a
  //  propósito: es la misma operación, no una distinta. Abrir la acción
  //  de cero (cerrar y volver a pulsar el botón) desmonta este
  //  componente y, al montarse de nuevo, genera una clave nueva.
  const [idOperacionCliente] = useState(() => crypto.randomUUID());
  //  Único de VERDAD por instancia montada — no basta con `linea.id`:
  //  tabla y tarjeta móvil pueden tener, las dos, un formulario abierto
  //  para la MISMA línea a la vez (conviven en el DOM, sólo CSS decide
  //  cuál se ve), y un `id` de atributo duplicado en el documento es
  //  HTML inválido además de romper `label[for]`.
  const idBase = useId();
  const [fechaEfectiva, setFechaEfectiva] = useState(ahoraParaInput);
  const borradores = useContext(ContextoBorradores);
  const claveBorrador = `${linea.id}:${tipo}`;
  const [recuperado] = useState(() => borradores?.get(claveBorrador) ?? null);
  const [observacion, setObservacionEstado] = useState(recuperado?.observacion ?? '');
  const [motivo, setMotivoEstado] = useState(recuperado?.motivo ?? '');
  function guardarBorrador(cambios: Partial<BorradorAccion>): void {
    if (borradores === null) return;
    const actual = borradores.get(claveBorrador) ?? { motivo: '', observacion: '' };
    const siguiente = { ...actual, ...cambios };
    if (siguiente.motivo === '' && siguiente.observacion === '') borradores.delete(claveBorrador);
    else borradores.set(claveBorrador, siguiente);
  }
  const setObservacion = (v: string): void => { setObservacionEstado(v); guardarBorrador({ observacion: v }); };
  const setMotivo = (v: string): void => { setMotivoEstado(v); guardarBorrador({ motivo: v }); };
  /** «Cancelar»: descartar a propósito — el único cierre que borra lo escrito. */
  function descartar(): void {
    borradores?.delete(claveBorrador);
    cerrar();
  }
  //  BUG 3 (UAT FASE 4.3): los asignados de ESTA línea vienen preseleccionados
  //  como PROPUESTA de ejecutores reales — nunca como el ejecutor real en sí
  //  (ASIGNADO ≠ EJECUTOR REAL sigue intacto: el Supervisor/Consultor puede
  //  quitar, añadir o corregir antes de enviar, y el backend sólo registra
  //  lo que de verdad se envía). Sin asignados, no se inventa ninguno.
  const [ejecutores, setEjecutores] = useState<string[]>(() =>
    tipo === 'confirmar' ? linea.asignados.map((a) => a.idOperario) : [],
  );
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const necesitaEjecutores = tipo === 'confirmar';
  const necesitaMotivo = tipo === 'no-ok' || tipo === 'no-aplica';
  //  F10.0 (0077): CONFIRMAR desde No OK es REHACER — la corrección realizada es obligatoria.
  const esRehacer = tipo === 'confirmar' && linea.estado === 'NO_OK';
  const panel = PANEL_ACCION[esRehacer ? 'rehacer' : tipo];
  const abrirNc = useContext(ContextoNcDeLinea);
  const { puede } = useSesion();

  function alternarEjecutor(id: string): void {
    setEjecutores((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function enviar(): Promise<void> {
    if (necesitaEjecutores && ejecutores.length === 0) {
      setError(new globalThis.Error('Selecciona al menos un ejecutor real: quién hizo el trabajo, no quién estaba asignado.'));
      return;
    }
    if (necesitaMotivo && motivo.trim() === '') {
      setError(new globalThis.Error('El motivo es obligatorio para esta acción.'));
      return;
    }
    if (esRehacer && observacion.trim() === '') {
      setError(new globalThis.Error('Describe la corrección realizada: es lo que deja trazado por qué la línea deja de estar No OK.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const cuerpo: Record<string, unknown> = {
        fechaEfectiva: new Date(fechaEfectiva).toISOString(),
        observacion: observacion.trim() === '' ? null : observacion.trim(),
        idOperacionCliente,
      };
      if (necesitaEjecutores) cuerpo['ejecutoresReales'] = ejecutores;
      if (necesitaMotivo) cuerpo['motivo'] = motivo.trim();

      await api.post<ResultadoAccionLinea>(`/api/ot-lineas/${linea.id}/${RUTA_ACCION[tipo]}`, cuerpo);
      borradores?.delete(claveBorrador);
      cerrar();
      recargarOt();
      //  F10.0: tras un No OK, el siguiente paso es su NC — se ofrece ya abierta, con la línea marcada.
      if (tipo === 'no-ok' && linea.ncAsociadas === 0 && abrirNc !== null && puede('nc:crear')) abrirNc(linea.id);
    } catch (e) {
      setError(e);
      recargarOt();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera">
        <h3>
          {panel.titulo} · línea {linea.orden}
        </h3>
        <button type="button" className="pequeno" style={ESTILO_TACTIL} onClick={descartar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {recuperado !== null && (
          <p className="nota" role="status">Se ha recuperado lo que habías escrito en esta acción.</p>
        )}

        {necesitaEjecutores && (
          <div className="campo">
            <span className="etiqueta-dato">Ejecutores reales (obligatorio)</span>
            <p className="pequena suave">
              Preseleccionados los asignados a esta línea, como propuesta — quítalos, añade otros o corrígelos:
              asignado ≠ ejecutor real.
            </p>
            {operarios === null ? (
              <Cargando texto="Cargando operarios…" />
            ) : operarios.length === 0 ? (
              <p className="suave pequena">No hay operarios habilitados en este centro.</p>
            ) : (
              <div className="lista-checks">
                {[...operarios]
                  .sort((a, b) => {
                    const aAsig = linea.asignados.some((x) => x.idOperario === a.id) ? 0 : 1;
                    const bAsig = linea.asignados.some((x) => x.idOperario === b.id) ? 0 : 1;
                    return aAsig - bAsig || a.codigo.localeCompare(b.codigo);
                  })
                  .map((op) => {
                    const esAsignado = linea.asignados.some((a) => a.idOperario === op.id);
                    return (
                      <label key={op.id} className="fila" style={{ gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={ejecutores.includes(op.id)}
                          onChange={() => alternarEjecutor(op.id)}
                        />
                        <span>
                          {op.nombre} {op.apellidos}
                        </span>
                        <span className="pequena tenue">({op.codigo})</span>
                        {esAsignado && <span className="etiqueta neutra pequena">asignado</span>}
                      </label>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {necesitaMotivo && (
          <div className="campo">
            <label htmlFor={`${idBase}-motivo`}>Motivo (obligatorio)</label>
            <textarea
              id={`${idBase}-motivo`}
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
        )}

        <div className="campo">
          <label htmlFor={`${idBase}-fecha`}>Fecha efectiva</label>
          <input
            id={`${idBase}-fecha`}
            type="datetime-local"
            value={fechaEfectiva}
            max={ahoraParaInput()}
            onChange={(e) => setFechaEfectiva(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor={`${idBase}-obs`}>{esRehacer ? 'Corrección realizada (obligatorio)' : 'Observación (opcional)'}</label>
          <textarea
            id={`${idBase}-obs`}
            rows={2}
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={CLASE_ACCION[tipo]}
          style={ESTILO_TACTIL}
          disabled={enviando}
          onClick={() => {
            void enviar();
          }}
        >
          {enviando ? 'Enviando…' : panel.envio}
        </button>
      </div>
    </div>
  );
}

/**
 * La cadena de reemisión.
 *
 * Es lo que 0023 hace visible: una ocurrencia anulada no se resucita, se
 * sustituye, y aquí se ve de dónde viene cada emisión y por qué.
 */
function Cadena({ ot }: { ot: OtDetalle }): ReactNode {
  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Cadena de reemisión</h2>
      </div>
      <div className="panel-cuerpo">
        <div className="cadena">
          {ot.cadena.map((c, i) => (
            <span key={c.id} className="fila" style={{ gap: 8 }}>
              {i > 0 && <span className="flecha">→</span>}
              {c.id === ot.id ? (
                //  El eslabón actual no enlaza a sí mismo: navegar a la
                //  misma ficha que ya se está viendo no es una acción real.
                <span className="eslabon actual" aria-current="page">
                  {c.codigo}
                </span>
              ) : (
                <Link className="eslabon" to={`/ots/${c.id}`}>
                  {c.codigo}
                </Link>
              )}
              <span className="pequena suave fila" style={{ gap: 4, display: 'inline-flex', alignItems: 'center' }}>
                em. {c.emision} · <EstadoOt estado={c.estado} />
              </span>
            </span>
          ))}
        </div>
        {ot.cadena.some((c) => c.motivo !== null) && (
          <ul className="pequena suave" style={{ marginTop: 10, paddingLeft: 18 }}>
            {ot.cadena
              .filter((c) => c.motivo !== null)
              .map((c) => (
                <li key={c.id}>
                  <span className="mono">{c.codigo}</span>: {c.motivo}
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function D({ e, v, a }: { e: string; v: string; a?: string }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{a !== undefined ? <Link to={a}>{v}</Link> : v}</div>
    </div>
  );
}
